import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
  UserMinus,
  X,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import {
  EVIDENCE_MEDIA_ACCEPT,
  attachEvidenceFromLibrary,
  uploadEvidence,
} from '@/features/evidence/api';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  FileSourceChooser,
  fileMatchesEvidenceMediaAccept,
  type FileLibrarySelection,
  type FileSourceMode,
} from '@/features/file-library';
import { localizeApiError } from '@/features/audit-issues';
import { useDismantlingMethodsList } from '@/features/masters/dismantling-methods/useDismantlingMethods';
import { useIncomingItemsList } from '@/features/masters/incoming-items/useIncomingItems';
import { useMaterialsList } from '@/features/masters/materials/useMaterials';
import type { Warehouse } from '@/features/masters/warehouses/types';
import { apiClient, type ApiSuccess } from '@/lib/api-client';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import {
  useAddDismantlingWorkEntry,
  useConfirmDismantlingIntermediate,
  useConfirmDismantlingIntermediatesBatch,
  useConfirmDismantlingSession,
  useCreateDismantlingSession,
  useDismantlingAddCandidates,
  useDismantlingSession,
  useUpdateDismantlingSession,
  useVoidDismantlingIntermediate,
  useVoidDismantlingWorkEntry,
} from './useDismantling';
import { fetchDismantlingSession } from './api';
import { CancelDismantlingSessionDialog } from './CancelDismantlingSessionDialog';
import {
  dismantlingIntermediateInputSchema,
  dismantlingOutputInputSchema,
  dismantlingOutputKindValues,
  dismantlingRouteValues,
  getDismantlingConfirmBlockers,
  getDismantlingOperationalStatus,
  positiveKgSchema,
  sessionHasNoRecordedOutput,
  strictlyPositiveKgSchema,
  type DismantlingConfirmBlockers,
  type DismantlingIntermediateDto,
  type DismantlingIntermediateInput,
  type DismantlingMethod,
  type DismantlingOutputInput,
  type DismantlingOutputKind,
  type DismantlingPackageDto,
  type DismantlingSessionDetailDto,
  type DismantlingWorkEntryDto,
} from './types';

const NS = 'operations';
// The worker-count field starts (and resets after each appended entry) at 0,
// so a settings-only save adds no work entry unless workers are entered.
const DEFAULT_WORKER_COUNT = 0;

const todayInBrowserTimezone = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface Props {
  batchId: string;
  /** The batch of pallets this dialog registers together (N >= 1). */
  pkgs: DismantlingPackageDto[];
  /**
   * Full per-package list rows from the list page, used as the candidate
   * pool for "add a pallet" when editing an existing imported session's
   * membership. Not needed for a brand-new (create) session.
   */
  candidatePkgs?: DismantlingPackageDto[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

/** Uniform shape for the top pallet list, from either the list DTO (new) or
 * the session detail DTO (existing). `package_seq` is only known for existing
 * sessions; the per-package list DTO does not carry it. */
interface DisplayPackage {
  inbound_package_id: string;
  package_seq: number | null;
  package_code: string;
  incoming_item_name: string;
  category_group: string | null;
  net_kg: string | null;
}

interface OutputRow {
  /** key local to the form — not sent to backend. */
  _key: string;
  output_kind: DismantlingOutputKind;
  material_id: string;
  raw_material_name: string;
  incoming_item_id: string;
  kg: string;
  warehouse_id: string | null;
  next_route_type: (typeof dismantlingRouteValues)[number];
  photo_evidence_file_ids: string[];
}

const emptyRow = (warehouseId: string | null = null): OutputRow => ({
  _key: Math.random().toString(36).slice(2),
  output_kind: 'final_material',
  material_id: '',
  raw_material_name: '',
  incoming_item_id: '',
  kg: '',
  warehouse_id: warehouseId,
  next_route_type: 'manual_dismantling',
  photo_evidence_file_ids: [],
});

/** A row the operator started filling in; untouched blank rows are skipped on save. */
const rowHasEnteredData = (row: OutputRow): boolean =>
  !!row.material_id ||
  !!row.incoming_item_id ||
  !!row.kg.trim() ||
  row.photo_evidence_file_ids.length > 0;

const INVALID_FIELD_CLASS = 'border-red-500 ring-2 ring-red-500';

type OutputFieldProblem = 'material' | 'item' | 'kg' | 'kg_format' | 'kg_positive' | 'warehouse';

/** What a started row still lacks, by the same rules the save payload is parsed with. */
const rowFieldProblems = (row: OutputRow): OutputFieldProblem[] => {
  const problems: OutputFieldProblem[] = [];
  const isFinal = row.output_kind === 'final_material';
  if (isFinal ? !row.material_id : !row.incoming_item_id)
    problems.push(isFinal ? 'material' : 'item');
  if (!row.kg.trim()) problems.push('kg');
  else if (!positiveKgSchema.safeParse(row.kg).success) problems.push('kg_format');
  else if (!isFinal && !strictlyPositiveKgSchema.safeParse(row.kg).success)
    problems.push('kg_positive');
  if (!row.warehouse_id) problems.push('warehouse');
  return problems;
};

/** Which input a problem belongs to; also the `data-output-field` suffix used to scroll to it. */
const problemInput = (problem: OutputFieldProblem): 'item' | 'kg' | 'warehouse' =>
  problem === 'material' || problem === 'item'
    ? 'item'
    : problem === 'warehouse'
      ? 'warehouse'
      : 'kg';

const parseFiniteKg = (value: string | null | undefined): number | null => {
  if (value == null || value.trim() === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

type Translate = ReturnType<typeof useTranslation>['t'];

/** One sentence per open reason why Complete cannot finish the session. */
const confirmBlockerReasons = (t: Translate, blockers: DismantlingConfirmBlockers): string[] => {
  const reasons: string[] = [];
  if (blockers.outputsMissingWarehouse > 0) {
    reasons.push(
      t('efs.operations.dismantling.errors.confirm_blocked_output_warehouse', {
        count: blockers.outputsMissingWarehouse,
        defaultValue: '{{count}}件の産出物に倉庫が設定されていません。倉庫を選択してください。',
      }),
    );
  }
  if (blockers.intermediatesMissingWarehouse > 0) {
    reasons.push(
      t('efs.operations.dismantling.errors.confirm_blocked_intermediate_warehouse', {
        count: blockers.intermediatesMissingWarehouse,
        defaultValue:
          '{{count}}件の中間品に倉庫が設定されていません。倉庫のない中間品は無効にしてから、倉庫を指定して登録し直してください。',
      }),
    );
  }
  if (blockers.draftIntermediates.length > 0) {
    reasons.push(
      t('efs.operations.dismantling.errors.confirm_blocked_draft_intermediates', {
        count: blockers.draftIntermediates.length,
        defaultValue:
          '{{count}}件の中間品が下書きのままです。上の「再解体する中間品」で「下書きをすべて確認」を押すか、1件ずつ無効にしてから、もう一度「完了」を押してください。',
      }),
    );
  }
  return reasons;
};

const kgToMilliKg = (kg: number | null | undefined): number | null =>
  kg == null || !Number.isFinite(kg) ? null : Math.round(kg * 1000);

const outputKgFromSessionDetail = (detail: DismantlingSessionDetailDto): number => {
  const finalMaterialKg = detail.outputs.reduce((acc, output) => {
    const kg = Number.parseFloat(output.kg);
    return Number.isFinite(kg) ? acc + kg : acc;
  }, 0);
  const intermediateRows =
    detail.intermediates.length > 0
      ? detail.intermediates
      : detail.work_entries.flatMap((entry) => entry.intermediate_items ?? []);
  const intermediateKg = intermediateRows.reduce((acc, intermediate) => {
    if (intermediate.status === 'voided') return acc;
    const kg = Number.parseFloat(intermediate.weight_kg);
    return Number.isFinite(kg) ? acc + kg : acc;
  }, 0);
  return finalMaterialKg + intermediateKg;
};

const inputKgFromSessionDetail = (detail: DismantlingSessionDetailDto): number | null => {
  const backendInput = parseFiniteKg(detail.input_weight_kg);
  if (backendInput != null && backendInput > 0) return backendInput;
  if (detail.packages.length === 0) return null;
  let total = 0;
  for (const pkg of detail.packages) {
    const kg = parseFiniteKg(pkg.net_kg);
    if (kg == null || kg <= 0) return null;
    total += kg;
  }
  return total > 0 ? total : null;
};

const kgReachedCompletion = (
  outputKg: number | null | undefined,
  inputKg: number | null | undefined,
): boolean => {
  const outputMilliKg = kgToMilliKg(outputKg);
  const inputMilliKg = kgToMilliKg(inputKg);
  return (
    outputMilliKg != null &&
    inputMilliKg != null &&
    inputMilliKg > 0 &&
    outputMilliKg >= inputMilliKg
  );
};

const detailReachedCompletion = (detail: DismantlingSessionDetailDto): boolean =>
  kgReachedCompletion(outputKgFromSessionDetail(detail), inputKgFromSessionDetail(detail));

const roundRatio = (value: number): number => Math.round(value * 100) / 100;

const sameOrderedIds = (nextIds: string[], currentIds: string[]): boolean =>
  nextIds.length === currentIds.length && nextIds.every((id, index) => id === currentIds[index]);

interface BatchFacilityDto {
  facility_id: string;
}

interface PhotoThumbProps {
  fileId: string;
  onRemove: () => void;
  removeLabel: string;
}

const PhotoThumb = ({ fileId, onRemove, removeLabel }: PhotoThumbProps) => (
  <div className="group relative inline-flex items-center gap-1 rounded-md border border-efs-border bg-efs-bg-subtle px-2 py-1 text-xs">
    <CheckCircle2 className="h-3.5 w-3.5 text-green-900" />
    <span className="max-w-[7rem] truncate font-mono">{fileId.slice(0, 8)}...</span>
    <button
      type="button"
      onClick={onRemove}
      aria-label={removeLabel}
      className="ml-1 rounded p-0.5 text-efs-text-muted hover:bg-efs-bg-panel hover:text-red-900"
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

/**
 * Link to a pallet derived by re-dismantling, preserving the `from` return
 * trail so 仕分けへ戻る can still restore the operator's sorting filters.
 */
export const buildDerivedPalletHref = (
  batchId: string | undefined,
  packageId: string,
  from: string | null,
) => {
  const params = new URLSearchParams({ packages: packageId });
  if (from) params.set('from', from);
  return `/batches/${batchId}/dismantling?${params.toString()}`;
};

export const DismantlingSessionDialog = ({
  batchId,
  pkgs,
  candidatePkgs = [],
  open,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const [searchParams] = useSearchParams();
  const derivedPalletHref = (packageId: string) =>
    buildDerivedPalletHref(batchId, packageId, searchParams.get('from'));
  const materialsQuery = useMaterialsList({ status: 'approved', per_page: 500 });
  const materials = materialsQuery.data?.rows ?? [];
  // Active only: saving an intermediate rejects a deactivated item (UNAPPROVED_MASTER).
  const incomingItemsQuery = useIncomingItemsList({
    status: 'approved',
    active: true,
    per_page: 500,
  });
  const materialOptions = useMemo(
    () =>
      (materialsQuery.data?.rows ?? []).map((m) => ({
        value: m.material_id,
        label: m.japanese_name,
        keywords: m.normalized_name,
      })),
    [materialsQuery.data?.rows],
  );
  const incomingItemOptions = useMemo(
    () =>
      (incomingItemsQuery.data?.rows ?? []).map((item) => ({
        value: item.incoming_item_id,
        label: item.japanese_name,
        keywords: item.normalized_name,
      })),
    [incomingItemsQuery.data?.rows],
  );
  const batchQuery = useQuery<BatchFacilityDto>({
    queryKey: ['monthly-batches', 'detail', batchId, 'dismantling-facility'],
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<BatchFacilityDto>>(
        `/monthly-batches/${batchId}`,
      );
      return response.data.data;
    },
    enabled: open && !!batchId,
  });
  const batchFacilityId = batchQuery.data?.facility_id;
  const warehousesQuery = useQuery<Warehouse[]>({
    queryKey: ['warehouses', 'dismantling', batchFacilityId],
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<Warehouse[]>>('/warehouses', {
        params: {
          active_only: true,
          facility_id: batchFacilityId,
          per_page: 500,
        },
      });
      return response.data.data ?? [];
    },
    enabled: open && !!batchFacilityId,
  });
  const warehouses = useMemo(
    () =>
      (warehousesQuery.data ?? []).filter(
        (warehouse) => !!batchFacilityId && warehouse.facility_id === batchFacilityId,
      ),
    [batchFacilityId, warehousesQuery.data],
  );

  const leadPkg = pkgs[0];
  const sharedSourceWarehouseId = useMemo(() => {
    const sourceWarehouseIds = new Set(
      pkgs.map((pkg) => pkg.warehouse_id).filter((id): id is string => !!id),
    );
    return sourceWarehouseIds.size === 1 && pkgs.every((pkg) => !!pkg.warehouse_id)
      ? (sourceWarehouseIds.values().next().value ?? null)
      : null;
  }, [pkgs]);
  const defaultWarehouseId =
    sharedSourceWarehouseId &&
    warehouses.some((warehouse) => warehouse.warehouse_id === sharedSourceWarehouseId)
      ? sharedSourceWarehouseId
      : null;
  const propSessionId = leadPkg?.dismantling_session_id ?? null;
  const [activeSessionId, setActiveSessionId] = useState<string | null>(propSessionId);
  // Creating the session shell and appending its first work entry are two API
  // calls. Keep a successfully-created shell off the rendered session state
  // until the work entry also succeeds; otherwise the session-reset effect
  // clears a long form before the operator can retry the failed second call.
  const provisionalSessionRef = useRef<DismantlingSessionDetailDto | null>(null);
  const [sessionSnapshot, setSessionSnapshot] =
    useState<ReturnType<typeof useDismantlingSession>['data']>(undefined);
  const detailQuery = useDismantlingSession(activeSessionId);
  const session = sessionSnapshot ?? detailQuery.data;
  const isExisting = !!activeSessionId;
  const operationalStatus = session ? getDismantlingOperationalStatus(session) : 'draft';
  const isConfirmed = operationalStatus === 'confirmed' || operationalStatus === 'locked';
  const needsLegacyOutputRepair =
    session?.status === 'imported' &&
    session.outputs.length > 0 &&
    session.work_entries.every((entry) => entry.is_legacy_backfill) &&
    session.outputs.every(
      (output) => output.output_state !== 'shipped' && output.output_state !== 'secondary_process',
    ) &&
    session.outputs.some((output) => !output.warehouse_id);
  // Amending a confirmed session: mis-recorded output is a real case, so the
  // outputs table can be reopened. Saving withdraws the booked inventory, drops
  // the session back to draft for re-confirmation and raises a warning
  // exception on the batch — see the backend's update_session.
  const [amendMode, setAmendMode] = useState(false);
  const fieldsLocked = isConfirmed && !amendMode;
  const cancelEligibleStatus = session?.status === 'imported' || session?.status === 'confirmed';
  const hasCancelBasePermission = hasPermission(user, 'dismantling.manage');
  const hasConfirmedCancelPermission =
    session?.status !== 'confirmed' || hasPermission(user, 'processing.dismantling.batch_confirm');
  const canCancelSession =
    isExisting &&
    !!session &&
    cancelEligibleStatus &&
    hasCancelBasePermission &&
    hasConfirmedCancelPermission;
  // Membership (which pallets belong to this session) can only be edited
  // while the session is still a draft ('imported'); confirmed/locked stay
  // read-only (backend rejects inbound_package_ids otherwise, 422).
  const canEditMembers = isExisting && session?.status === 'imported';

  useEffect(() => {
    if (!open) {
      provisionalSessionRef.current = null;
      return;
    }
    // Creating the shell invalidates the pallet list. That refetch can update
    // `propSessionId` before the append request settles. When it merely echoes
    // our provisional shell, keep the current form mounted; treating it as a
    // newly selected session would reproduce the incident and wipe the rows.
    if (
      provisionalSessionRef.current?.dismantling_session_id &&
      provisionalSessionRef.current.dismantling_session_id === propSessionId
    ) {
      return;
    }
    provisionalSessionRef.current = null;
    setActiveSessionId(propSessionId);
    setSessionSnapshot(undefined);
    setAmendMode(false);
  }, [open, propSessionId]);

  /** Reopen the confirmed outputs for correction, seeded with what was booked. */
  const startAmend = () => {
    if (!session) return;
    setOutputs(
      session.outputs.length > 0
        ? session.outputs.map((output) => ({
            _key: output.dismantling_output_id,
            output_kind: 'final_material' as const,
            material_id: output.material_id,
            raw_material_name: output.raw_material_name,
            incoming_item_id: '',
            kg: output.kg,
            warehouse_id: output.warehouse_id ?? null,
            next_route_type: 'manual_dismantling' as const,
            photo_evidence_file_ids: output.photo_evidence_file_ids ?? [],
          }))
        : [emptyRow(defaultWarehouseId)],
    );
    setChangeReason('');
    setErrorMsg(null);
    setAmendMode(true);
  };

  /**
   * Leave amend mode, whether cancelled or saved, putting the form back to
   * `detail`: the booked session on cancel, the saved one after a save.
   * - The seeded rows must go: once amendMode is off the booked outputs count
   *   again, and rows left in the hidden table were added on top of them
   *   (cancelling showed 1179 kg booked as 2359). A saved correction also
   *   comes back as 'imported', which resets the form anyway; clearing here
   *   keeps that from depending on the status.
   * - Method and notes edited while amending stay in state otherwise, show on
   *   the read-only view, and ride along on the next amendment's save.
   * - Errors from the abandoned attempt point at fields no longer shown.
   */
  const leaveAmendMode = (detail: DismantlingSessionDetailDto | undefined = session) => {
    setAmendMode(false);
    setOutputs([emptyRow(defaultWarehouseId)]);
    setFlaggedProblems(new Map());
    if (detail) {
      setMethod(detail.dismantling_method as DismantlingMethod);
      setSessionNotes(detail.notes ?? '');
    }
    setChangeReason('');
    setErrorMsg(null);
    setCompleteBlocker(null);
  };

  useEffect(() => {
    if (detailQuery.data) setSessionSnapshot(detailQuery.data);
  }, [detailQuery.data]);

  // The list page passes only its current page; load every unassigned pallet
  // of the batch so a truck spread over several pages can still be grouped.
  const addCandidatesQuery = useDismantlingAddCandidates(batchId, open && canEditMembers);
  const candidatePool = useMemo(() => {
    const byId = new Map(candidatePkgs.map((p) => [p.inbound_package_id, p]));
    for (const pkg of addCandidatesQuery.data ?? []) {
      if (!byId.has(pkg.inbound_package_id)) byId.set(pkg.inbound_package_id, pkg);
    }
    return [...byId.values()];
  }, [addCandidatesQuery.data, candidatePkgs]);

  // Lookup of every candidate pallet by id, used both to resolve newly-added
  // members' display fields and to build the "add a pallet" picker.
  const candidateById = useMemo(
    () => new Map(candidatePool.map((p) => [p.inbound_package_id, p])),
    [candidatePool],
  );

  // Editable working set of member pallet ids. Only meaningful while
  // canEditMembers; null means "not editing" (derive display straight from
  // session.packages / pkgs instead).
  const [memberIds, setMemberIds] = useState<string[] | null>(null);
  const [addPickerId, setAddPickerId] = useState<string>('');

  // Reset the editable membership set whenever the session (re)loads or the
  // dialog opens fresh.
  useEffect(() => {
    if (!open) {
      setMemberIds(null);
      setAddPickerId('');
      return;
    }
    if (session) {
      setMemberIds(session.packages.map((p) => p.inbound_package_id));
      setAddPickerId('');
    }
  }, [open, session]);

  // Pallet list shown at the top. For an existing session it reflects the
  // editable membership set (session.packages for known members, candidate
  // rows for newly-added ones); for a new batch it comes from the selected
  // list rows (no package_seq).
  const displayPackages = useMemo<DisplayPackage[]>(() => {
    if (isExisting && session) {
      const byId = new Map(session.packages.map((p) => [p.inbound_package_id, p]));
      const ids = memberIds ?? session.packages.map((p) => p.inbound_package_id);
      return ids.map((id): DisplayPackage => {
        const known = byId.get(id);
        if (known) {
          return {
            inbound_package_id: known.inbound_package_id,
            package_seq: known.package_seq,
            package_code: known.package_code,
            incoming_item_name: known.incoming_item_name,
            category_group: known.category_group ?? null,
            net_kg: known.net_kg ?? null,
          };
        }
        // Newly-added member: not yet in the session detail DTO, resolve
        // from the candidate (list-page) rows instead.
        const candidate = candidateById.get(id);
        return {
          inbound_package_id: id,
          package_seq: null,
          package_code: candidate?.package_code ?? id,
          incoming_item_name: candidate?.incoming_item_name ?? '',
          category_group: candidate?.category_group ?? null,
          net_kg: candidate?.net_kg ?? null,
        };
      });
    }
    return pkgs.map((p) => ({
      inbound_package_id: p.inbound_package_id,
      package_seq: null,
      package_code: p.package_code,
      incoming_item_name: p.incoming_item_name,
      category_group: p.category_group ?? null,
      net_kg: p.net_kg ?? null,
    }));
  }, [isExisting, session, memberIds, candidateById, pkgs]);

  // Σ of net_kg over pallets that carry a weight; missing-weight pallets are
  // flagged and excluded (soft reconciliation only — never blocks submit).
  const totalPackageKg = useMemo(
    () =>
      displayPackages.reduce((acc, p) => {
        const n = Number.parseFloat(p.net_kg ?? '');
        return Number.isFinite(n) ? acc + n : acc;
      }, 0),
    [displayPackages],
  );

  // Candidates for "add a pallet": unoccupied, same monthly_batch (implicit —
  // both sources are this batch's pallets), and not already a
  // member. Category is not a constraint because mixed pallets can contain
  // material that needs the same manual dismantling session.
  const addableCandidates = useMemo(() => {
    if (!canEditMembers) return [];
    const memberSet = new Set(memberIds ?? []);
    return candidatePool.filter((p) => {
      if (memberSet.has(p.inbound_package_id)) return false;
      if (p.dismantling_session_id) return false;
      return true;
    });
  }, [canEditMembers, candidatePool, memberIds]);

  const removeMember = (id: string) => {
    setMemberIds((prev) => {
      const current = prev ?? session?.packages.map((p) => p.inbound_package_id) ?? [];
      if (current.length <= 1) return current; // never allow emptying to 0
      return current.filter((pid) => pid !== id);
    });
  };

  const addMember = (id: string) => {
    if (!id) return;
    setMemberIds((prev) => {
      const current = prev ?? session?.packages.map((p) => p.inbound_package_id) ?? [];
      if (current.includes(id)) return current;
      return [...current, id];
    });
    setAddPickerId('');
  };

  const create = useCreateDismantlingSession();
  const update = useUpdateDismantlingSession();
  const addWorkEntry = useAddDismantlingWorkEntry();
  const confirm = useConfirmDismantlingSession();
  const confirmIntermediate = useConfirmDismantlingIntermediate();
  const confirmIntermediatesBatch = useConfirmDismantlingIntermediatesBatch();
  const voidIntermediate = useVoidDismantlingIntermediate();
  const voidWorkEntry = useVoidDismantlingWorkEntry();
  // The 0 kg history row whose void reason is being entered, if any.
  const [voidingEntryId, setVoidingEntryId] = useState<string | null>(null);
  const [entryVoidReason, setEntryVoidReason] = useState('');
  const [entryVoidError, setEntryVoidError] = useState<string | null>(null);

  const [date, setDate] = useState<string>(todayInBrowserTimezone);
  // 0 workers: Save stores only the settings; a work entry needs at least 1.
  const [workerCount, setWorkerCount] = useState<number>(DEFAULT_WORKER_COUNT);
  const [workerCountInvalid, setWorkerCountInvalid] = useState(false);
  // The processor's own methods. Routing no longer says which one applies, so
  // the first active method is the opening assumption and the operator picks.
  // Scoped to the BATCH, not to the signed-in user's company: the processor
  // doing the work owns the vocabulary, and it is the same list the server
  // validates against. Retired methods come back too, so a session that
  // already names one can still be labelled instead of showing a raw code.
  const methodsQuery = useDismantlingMethodsList(batchId ? { monthly_batch_id: batchId } : {});
  const allMethods = useMemo(() => methodsQuery.data ?? [], [methodsQuery.data]);
  const methodOptions = useMemo(() => allMethods.filter((m) => m.active_flag), [allMethods]);
  const [method, setMethod] = useState<DismantlingMethod>('');
  /**
   * The method this session already names, when the master no longer offers
   * it — retired, or written before the master existed at all. Without this
   * the Select would find no matching item and render blank, which reads as
   * "no method recorded" on a session that has one. Falls back to the stored
   * code as its own label when even the master has never heard of it.
   */
  const storedMethodOption = useMemo(() => {
    if (!method || methodOptions.some((m) => m.method_code === method)) return null;
    const retired = allMethods.find((m) => m.method_code === method);
    return { method_code: method, display_name: retired?.display_name ?? method };
  }, [allMethods, method, methodOptions]);
  const [sessionNotes, setSessionNotes] = useState<string>('');
  const [workNotes, setWorkNotes] = useState<string>('');
  const [changeReason, setChangeReason] = useState<string>('');
  const [intermediateActionReason, setIntermediateActionReason] = useState<string>('');
  const [outputs, setOutputs] = useState<OutputRow[]>([emptyRow(defaultWarehouseId)]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Why Complete stopped. Separate from `errorMsg`, which the session reset
  // effect clears when the first Complete creates the session.
  const [completeBlocker, setCompleteBlocker] = useState<string | null>(null);
  const [confirmPrompt, setConfirmPrompt] = useState<{
    sessionId: string;
    drafts: DismantlingIntermediateDto[];
  } | null>(null);
  const [intermediatesAttention, setIntermediatesAttention] = useState(false);
  const intermediatesSectionRef = useRef<HTMLDivElement | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [uploadingOutputKey, setUploadingOutputKey] = useState<string | null>(null);
  const [outputPhotoSourceModes, setOutputPhotoSourceModes] = useState<
    Record<string, FileSourceMode>
  >({});
  const [libraryPickerOutputKey, setLibraryPickerOutputKey] = useState<string | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // What each row lacked when the last Save/Complete stopped on it. Shown are
  // only those problems that are still there, so filling a field, clearing the
  // row or deleting it takes them off without another click; a problem that
  // appears mid-typing afterwards (say "1." on the way to "1.5") waits for the
  // next save instead of flashing up while the operator types.
  const [flaggedProblems, setFlaggedProblems] = useState<Map<string, Set<OutputFieldProblem>>>(
    () => new Map(),
  );
  const rowsWithProblems = outputs.flatMap((o, index) => {
    const flagged = flaggedProblems.get(o._key);
    if (!flagged || !rowHasEnteredData(o)) return [];
    const problems = rowFieldProblems(o).filter((p) => flagged.has(p));
    return problems.length > 0 ? [{ key: o._key, rowNumber: index + 1, problems }] : [];
  });
  const invalidInputs = new Set(
    rowsWithProblems.flatMap((row) => row.problems.map((p) => `${row.key}:${problemInput(p)}`)),
  );
  const fieldProblemLabel = (problem: OutputFieldProblem): string => {
    switch (problem) {
      case 'material':
        return t('efs.operations.dismantling.errors.output_field_problems.material', {
          defaultValue: '素材が未選択',
        });
      case 'item':
        return t('efs.operations.dismantling.errors.output_field_problems.item', {
          defaultValue: '品目が未選択',
        });
      case 'kg':
        return t('efs.operations.dismantling.errors.output_field_problems.kg', {
          defaultValue: 'kgが未入力',
        });
      case 'kg_format':
        return t('efs.operations.dismantling.errors.output_field_problems.kg_format', {
          defaultValue: 'kgは0以上・小数点3桁までで入力',
        });
      case 'kg_positive':
        return t('efs.operations.dismantling.errors.output_field_problems.kg_positive', {
          defaultValue: 'kgは0より大きい値で入力',
        });
      case 'warehouse':
        return t('efs.operations.dismantling.errors.output_field_problems.warehouse', {
          defaultValue: '倉庫が未選択',
        });
    }
  };
  const canReadFileLibrary =
    hasPermission(user, 'file_library.read') || hasPermission(user, 'file_library.manage');

  // Existing outputs are immutable history once real daily entries exist.
  // The only exception is a migrated draft whose legacy output lacks the
  // evidence/location needed for confirmation; expose those rows once for an
  // audited repair before switching back to append-only daily work.
  useEffect(() => {
    if (!open) return;
    if (session) {
      setDate(todayInBrowserTimezone());
      setWorkerCount(DEFAULT_WORKER_COUNT);
      setMethod(session.dismantling_method as DismantlingMethod);
      setSessionNotes(session.notes ?? '');
      setWorkNotes('');
      setOutputs(
        needsLegacyOutputRepair
          ? session.outputs.map((output) => ({
              _key: output.dismantling_output_id,
              output_kind: 'final_material',
              material_id: output.material_id,
              raw_material_name: output.raw_material_name,
              incoming_item_id: '',
              kg: output.kg,
              warehouse_id: output.warehouse_id ?? null,
              next_route_type: 'manual_dismantling',
              photo_evidence_file_ids: output.photo_evidence_file_ids ?? [],
            }))
          : [emptyRow()],
      );
      setChangeReason('');
    } else if (!isExisting) {
      setDate(todayInBrowserTimezone());
      setWorkerCount(DEFAULT_WORKER_COUNT);
      setMethod('');
      setSessionNotes('');
      setWorkNotes('');
      setOutputs([emptyRow()]);
      setChangeReason('');
    }
    setErrorMsg(null);
    // Keyed on WHICH session and what state it is in, not on the object
    // identity. `session` is a snapshot that the detail query later replaces
    // with an equal value, and it refetches in the background; depending on the
    // reference re-ran this reset at unpredictable moments and wiped whatever
    // the operator had already typed into the daily entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExisting, needsLegacyOutputRepair, open, session?.dismantling_session_id, session?.status]);

  // The method list can finish loading after the dialog opens. Fill only a
  // still-empty method rather than resetting the form, so an operator who
  // started typing does not lose what they entered. A company that has defined
  // no methods falls back to the same plain code the server assumes, so the
  // work can be registered before anyone visits the master screen.
  useEffect(() => {
    if (!open || methodsQuery.isLoading || methodOptions.length === 0) return;
    setMethod((current) => (current === '' ? methodOptions[0].method_code : current));
  }, [methodOptions, methodsQuery.isLoading, open]);

  // Warehouse lookup can finish after the operator starts entering the row.
  // Fill only still-empty warehouse fields instead of resetting the whole form.
  useEffect(() => {
    if (!open || !defaultWarehouseId || isConfirmed) return;
    setOutputs((previous) =>
      previous.map((output) =>
        output.warehouse_id ? output : { ...output, warehouse_id: defaultWarehouseId },
      ),
    );
  }, [defaultWarehouseId, isConfirmed, open, session?.dismantling_session_id]);

  const totalKg = useMemo(
    () =>
      outputs.reduce((acc, o) => {
        const n = Number.parseFloat(o.kg);
        return Number.isFinite(n) ? acc + n : acc;
      }, 0),
    [outputs],
  );

  // Existing sessions trust the backend denominator only. Unlike
  // inputKgFromSessionDetail there is no fallback to package weights here, so
  // a missing input_weight_kg keeps Complete disabled instead of guessing.
  const inputWeight = useMemo(() => {
    const sessionMemberIds = session?.packages.map((pkg) => pkg.inbound_package_id) ?? [];
    const membershipChanged =
      !!session && memberIds != null && !sameOrderedIds(memberIds, sessionMemberIds);
    const deriveFromDisplayPackages = () => {
      if (displayPackages.length === 0) return null;
      let sum = 0;
      for (const pkg of displayPackages) {
        const kg = parseFiniteKg(pkg.net_kg);
        if (kg == null || kg <= 0) return null;
        sum += kg;
      }
      return sum > 0 ? sum : null;
    };

    if (!isExisting || membershipChanged) return deriveFromDisplayPackages();

    if (session) {
      const backendInput = parseFiniteKg(session.input_weight_kg);
      if (backendInput != null && backendInput > 0) return backendInput;
      return null;
    }

    return deriveFromDisplayPackages();
  }, [displayPackages, isExisting, memberIds, session]);

  const formatKg = (kg: number | null | undefined): string =>
    kg == null || !Number.isFinite(kg) ? '—' : kg.toFixed(3);

  const formatRatio = (ratio: number | null | undefined): string =>
    ratio == null || !Number.isFinite(ratio) ? '—' : `${ratio.toFixed(2)}%`;

  const ratioForKg = useCallback(
    (kg: number): number | null =>
      inputWeight == null ? null : roundRatio((kg / inputWeight) * 100),
    [inputWeight],
  );

  // Amending (and legacy repair) seeds the editable table FROM the booked
  // outputs, so those rows are about to replace them rather than add to them.
  // Counting both sides put the same weight in twice: a session that
  // reconciled exactly would show a difference equal to its own output total
  // the moment 修正 was pressed. Intermediates are never seeded, so they stay
  // in the history either way.
  const editableRowsReplaceOutputs = amendMode || needsLegacyOutputRepair;

  const historicalKg = useMemo(() => {
    const finalMaterialKg = editableRowsReplaceOutputs
      ? 0
      : (session?.outputs ?? []).reduce((acc, output) => {
          const kg = Number.parseFloat(output.kg);
          return Number.isFinite(kg) ? acc + kg : acc;
        }, 0);
    const intermediateRows =
      (session?.intermediates?.length ?? 0) > 0
        ? (session?.intermediates ?? [])
        : (session?.work_entries ?? []).flatMap((entry) => entry.intermediate_items ?? []);
    const intermediateKg = intermediateRows.reduce((acc, intermediate) => {
      if (intermediate.status === 'voided') return acc;
      const kg = Number.parseFloat(intermediate.weight_kg);
      return Number.isFinite(kg) ? acc + kg : acc;
    }, 0);
    return finalMaterialKg + intermediateKg;
  }, [editableRowsReplaceOutputs, session?.intermediates, session?.outputs, session?.work_entries]);

  const currentOutputKg = historicalKg;
  const projectedOutputKg = historicalKg + totalKg;
  const currentProgress = session?.progress_percent ?? ratioForKg(currentOutputKg);
  const pendingProgress = ratioForKg(totalKg);
  const projectedProgress = ratioForKg(projectedOutputKg);
  const projectedOverweight = projectedProgress != null && projectedProgress > 100;
  const projectedWeightComplete = kgReachedCompletion(projectedOutputKg, inputWeight);
  const regularWorkflowVisible = !isConfirmed && !amendMode && !needsLegacyOutputRepair;
  const regularSaveDisabledByProgress = regularWorkflowVisible && projectedWeightComplete;
  const regularCompleteDisabledByProgress = regularWorkflowVisible && !projectedWeightComplete;
  // Completed sessions, and unfinished ones with no recorded output weight
  // (dismantling started on the wrong pallet): same rule as the list rows.
  const showReturnToSortingAction =
    !amendMode &&
    canCancelSession &&
    (isConfirmed || (session?.status === 'imported' && sessionHasNoRecordedOutput(session)));
  const completionDisabledReason =
    inputWeight == null
      ? t('efs.operations.dismantling.dialog.weight_stats.complete_unavailable', {
          defaultValue:
            '元パレット重量が未入力または0以下のため、完了判定ができません。重量を確認してください。',
        })
      : !projectedWeightComplete
        ? t('efs.operations.dismantling.dialog.weight_stats.complete_needs_100', {
            input: formatKg(inputWeight),
            projectedKg: formatKg(projectedOutputKg),
            projected: formatRatio(projectedProgress),
            defaultValue:
              '完了は累積産出が元パレット重量以上になってから押せます（{{projectedKg}} / {{input}} kg、{{projected}}）。',
          })
        : null;

  const saveDisabledReason = regularSaveDisabledByProgress
    ? t('efs.operations.dismantling.errors.complete_instead_of_save', {
        defaultValue: '累積産出が100%に達するため、保存ではなく完了してください。',
      })
    : undefined;

  const historyRows = useMemo(() => {
    let cumulativeKg = 0;
    return (session?.work_entries ?? []).map((entry) => {
      const finalKg = entry.outputs.reduce((sum, output) => {
        const value = Number.parseFloat(output.kg);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      const intermediateKg = (entry.intermediate_items ?? []).reduce((sum, intermediate) => {
        if (intermediate.status === 'voided') return sum;
        const value = Number.parseFloat(intermediate.weight_kg);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
      const entryKg = parseFiniteKg(entry.output_weight_kg) ?? finalKg + intermediateKg;
      cumulativeKg = parseFiniteKg(entry.cumulative_output_weight_kg) ?? cumulativeKg + entryKg;
      return {
        ...entry,
        cumulativeKg,
        kg: entryKg,
        incrementRatio: entry.progress_increment_percent ?? ratioForKg(entryKg),
        cumulativeRatio: entry.cumulative_progress_percent ?? ratioForKg(cumulativeKg),
        outputCount: entry.outputs.length + (entry.intermediate_items ?? []).length,
      };
    });
  }, [ratioForKg, session?.work_entries]);

  // Every output already recorded on this session, stamped with the work date
  // it belongs to. Shown read-only so confirmed work still lists what it
  // produced — the editable table below is for appending new daily work.
  const recordedOutputs = useMemo(() => {
    if (!session || needsLegacyOutputRepair) return [];
    const dateByEntry = new Map(
      session.work_entries.map((entry) => [entry.dismantling_work_entry_id, entry.work_date]),
    );
    return session.outputs.map((output) => ({
      ...output,
      workDate: output.work_entry_id ? (dateByEntry.get(output.work_entry_id) ?? null) : null,
    }));
  }, [needsLegacyOutputRepair, session]);

  const sessionIntermediates = useMemo(() => {
    if (!session) return [];
    return (session.intermediates ?? []).length > 0
      ? (session.intermediates ?? [])
      : session.work_entries.flatMap((entry) => entry.intermediate_items ?? []);
  }, [session]);

  // Point the operator at the intermediates section while it holds the reason
  // Complete stopped, and drop the highlight once nothing there is pending.
  useEffect(() => {
    if (intermediatesAttention) {
      intermediatesSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [intermediatesAttention]);
  useEffect(() => {
    if (!intermediatesAttention) return;
    const pending = sessionIntermediates.some(
      (item) => item.status === 'draft' || (item.status !== 'voided' && !item.warehouse_id),
    );
    if (!pending) {
      setIntermediatesAttention(false);
      // Keep whatever still blocks Complete (e.g. an output without a
      // warehouse); only the intermediate part of the message is resolved.
      const remaining = session
        ? confirmBlockerReasons(t, getDismantlingConfirmBlockers(session))
        : [];
      setCompleteBlocker(remaining.length > 0 ? remaining.join(' ') : null);
    }
  }, [intermediatesAttention, sessionIntermediates, session, t]);
  useEffect(() => {
    if (open) return;
    setCompleteBlocker(null);
    setIntermediatesAttention(false);
    setConfirmPrompt(null);
    setVoidingEntryId(null);
    setEntryVoidReason('');
    setEntryVoidError(null);
  }, [open]);
  const hasOnlyLegacyBackfillEntries =
    !!session &&
    session.work_entries.length > 0 &&
    session.work_entries.every((entry) => entry.is_legacy_backfill);
  const hasLegacyOutputlessCompleteProgress =
    !!session &&
    session.progress_percent != null &&
    session.progress_percent >= 100 &&
    session.outputs.length === 0;
  const isOutputlessCompleteSession =
    session?.status === 'imported' &&
    (hasOnlyLegacyBackfillEntries || hasLegacyOutputlessCompleteProgress) &&
    session.outputs.length === 0 &&
    sessionIntermediates.every((item) => item.status === 'voided');

  const updateOutput = (key: string, patch: Partial<OutputRow>) => {
    setOutputs((prev) => prev.map((o) => (o._key === key ? { ...o, ...patch } : o)));
  };

  const removeOutput = (key: string) => {
    setOutputs((prev) => (prev.length <= 1 ? prev : prev.filter((o) => o._key !== key)));
  };

  const addOutput = () => {
    setOutputs((prev) => [...prev, emptyRow(defaultWarehouseId)]);
  };

  const handleOutputPhotos = async (key: string, files: FileList) => {
    if (!batchId) {
      setErrorMsg('monthly summary sheet ID is missing');
      return;
    }
    setUploadingOutputKey(key);
    setErrorMsg(null);
    try {
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        const ev = await uploadEvidence(batchId, file, 'photo');
        uploaded.push(ev.evidence_file_id);
      }
      setOutputs((prev) =>
        prev.map((o) =>
          o._key === key
            ? {
                ...o,
                photo_evidence_file_ids: [...(o.photo_evidence_file_ids ?? []), ...uploaded],
              }
            : o,
        ),
      );
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadingOutputKey(null);
    }
  };

  const handleOutputLibraryPhoto = async (key: string, file: FileLibrarySelection) => {
    if (!batchId) {
      setErrorMsg('monthly summary sheet ID is missing');
      return;
    }
    setUploadingOutputKey(key);
    setErrorMsg(null);
    try {
      const ev = await attachEvidenceFromLibrary({
        batchId,
        companyFileId: file.company_file_id,
        evidenceType: 'photo',
      });
      setOutputs((prev) =>
        prev.map((o) =>
          o._key === key
            ? {
                ...o,
                photo_evidence_file_ids: [
                  ...(o.photo_evidence_file_ids ?? []),
                  ev.evidence_file_id,
                ],
              }
            : o,
        ),
      );
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setUploadingOutputKey(null);
    }
  };

  const removeOutputPhoto = (key: string, fileId: string) => {
    setOutputs((prev) =>
      prev.map((o) =>
        o._key === key
          ? {
              ...o,
              photo_evidence_file_ids: (o.photo_evidence_file_ids ?? []).filter(
                (id) => id !== fileId,
              ),
            }
          : o,
      ),
    );
  };

  const validOutputs = (): {
    outputs: DismantlingOutputInput[];
    intermediate_items: DismantlingIntermediateInput[];
  } | null => {
    const finalOutputs: DismantlingOutputInput[] = [];
    const intermediateItems: DismantlingIntermediateInput[] = [];
    // Name every incomplete row and what it lacks at once, then take the
    // operator to the first gap: a bare "select a warehouse" under a long list
    // left them deleting and re-entering rows to find it (field report 2026-10-19).
    const incomplete = outputs.filter(
      (o) => rowHasEnteredData(o) && rowFieldProblems(o).length > 0,
    );
    setFlaggedProblems(new Map(incomplete.map((o) => [o._key, new Set(rowFieldProblems(o))])));
    if (incomplete.length > 0) {
      const first = incomplete[0];
      const target = `${first._key}:${problemInput(rowFieldProblems(first)[0])}`;
      requestAnimationFrame(() => {
        const field = document.querySelector<HTMLElement>(`[data-output-field="${target}"]`);
        field?.scrollIntoView?.({ block: 'center' });
        // The item picker is wrapped in a marker div; focus its button.
        const focusable = field?.matches('button, input')
          ? field
          : field?.querySelector<HTMLElement>('button, input');
        focusable?.focus({ preventScroll: true });
      });
      return null;
    }
    for (const o of outputs) {
      if (!rowHasEnteredData(o)) continue;

      if (o.output_kind === 'final_material') {
        const candidate = {
          output_kind: 'final_material' as const,
          material_id: o.material_id,
          raw_material_name:
            o.raw_material_name ||
            materials.find((material) => material.material_id === o.material_id)?.japanese_name ||
            '',
          kg: o.kg,
          warehouse_id: o.warehouse_id ?? '',
          photo_evidence_file_ids: o.photo_evidence_file_ids,
        };
        const parsed = dismantlingOutputInputSchema.safeParse(candidate);
        if (!parsed.success) {
          setErrorMsg(parsed.error.issues[0]?.message ?? 'invalid final material output');
          return null;
        }
        finalOutputs.push(parsed.data);
        continue;
      }

      const candidate = {
        incoming_item_id: o.incoming_item_id,
        weight_kg: o.kg,
        warehouse_id: o.warehouse_id ?? '',
        next_route_type: o.next_route_type,
        photo_evidence_file_ids: o.photo_evidence_file_ids,
      };
      const parsed = dismantlingIntermediateInputSchema.safeParse(candidate);
      if (!parsed.success) {
        setErrorMsg(parsed.error.issues[0]?.message ?? 'invalid dismantling intermediate');
        return null;
      }
      intermediateItems.push(parsed.data);
    }
    return { outputs: finalOutputs, intermediate_items: intermediateItems };
  };

  /** Confirm (material receipt) requires a physical warehouse on every output. */
  const allOutputsHaveWarehouse = (rows: DismantlingOutputInput[]): boolean =>
    rows.every((output) => !!output.warehouse_id);

  /** Membership payload for an update: only sent while the session is still
   * 'imported' (draft) — confirmed/locked sessions reject this field (422).
   * Guards the "never empty to 0" rule as a last line of defense (the
   * remove-member handler already refuses to drop below 1). */
  const memberIdsForUpdate = (): string[] | undefined => {
    if (!canEditMembers || memberIds == null) return undefined;
    return memberIds;
  };

  const metadataHasChanged = (detail = session): boolean => {
    if (!detail) return false;
    const nextMemberIds = memberIdsForUpdate();
    const currentMemberIds = detail.packages.map((pkg) => pkg.inbound_package_id);
    return (
      (nextMemberIds != null && !sameOrderedIds(nextMemberIds, currentMemberIds)) ||
      method !== detail.dismantling_method ||
      sessionNotes !== (detail.notes ?? '')
    );
  };

  const createContainer = async () => {
    if (provisionalSessionRef.current) return provisionalSessionRef.current;
    const created = await create.mutateAsync({
      inbound_package_ids: pkgs.map((pkg) => pkg.inbound_package_id),
      dismantling_date: date,
      worker_count: workerCount,
      dismantling_method: method,
      outputs: [],
      notes: sessionNotes || undefined,
    });
    provisionalSessionRef.current = created;
    return created;
  };

  const saveMetadata = async (detail: NonNullable<typeof session>) => {
    if (!metadataHasChanged(detail)) return detail;
    if (!changeReason.trim()) {
      setErrorMsg(t('efs.operations.dismantling.errors.reason_required'));
      return null;
    }
    const nextMemberIds = memberIdsForUpdate();
    const updated = await update.mutateAsync({
      id: detail.dismantling_session_id,
      body: {
        ...(nextMemberIds ? { inbound_package_ids: nextMemberIds } : {}),
        dismantling_method: method,
        notes: sessionNotes || undefined,
        change_reason: changeReason.trim(),
      },
    });
    setSessionSnapshot(updated);
    setChangeReason('');
    return updated;
  };

  const repairLegacyOutputs = async (detail: NonNullable<typeof session>) => {
    const cleaned = validOutputs();
    if (!cleaned) return null;
    if (cleaned.outputs.length !== detail.outputs.length || cleaned.intermediate_items.length > 0) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.legacy_outputs_required', {
          defaultValue: '舊產出修復時不可刪除既有產出物',
        }),
      );
      return null;
    }
    if (!allOutputsHaveWarehouse(cleaned.outputs)) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.warehouse_required', {
          defaultValue: '各産出物には実在する倉庫が必要です',
        }),
      );
      return null;
    }
    if (!changeReason.trim()) {
      setErrorMsg(t('efs.operations.dismantling.errors.reason_required'));
      return null;
    }
    const nextMemberIds = memberIdsForUpdate();
    const updated = await update.mutateAsync({
      id: detail.dismantling_session_id,
      body: {
        ...(nextMemberIds ? { inbound_package_ids: nextMemberIds } : {}),
        dismantling_method: method,
        outputs: cleaned.outputs,
        notes: sessionNotes || undefined,
        change_reason: changeReason.trim(),
      },
    });
    setSessionSnapshot(updated);
    setChangeReason('');
    return updated;
  };

  const preparePendingEntry = () => {
    // A processor with no registered methods types its own label, so the field
    // can genuinely be empty. Say so plainly — left to fall through, an empty
    // method reads as a metadata change and the operator gets asked for a
    // change reason instead, which explains nothing.
    if (!method.trim()) {
      setErrorMsg(t('efs.operations.dismantling.errors.method_required'));
      return null;
    }
    const cleaned = validOutputs();
    if (!cleaned) return null;
    return cleaned;
  };

  const pendingHasOutputs = (
    pending: NonNullable<ReturnType<typeof preparePendingEntry>>,
  ): boolean => pending.outputs.length > 0 || pending.intermediate_items.length > 0;

  const appendPendingEntry = async (
    detail: NonNullable<typeof session>,
    pending: NonNullable<ReturnType<typeof preparePendingEntry>>,
    options: { allowEmptyWorkEntry?: boolean } = {},
  ) => {
    if (!pendingHasOutputs(pending) && !options.allowEmptyWorkEntry) return detail;
    const appended = await addWorkEntry.mutateAsync({
      id: detail.dismantling_session_id,
      body: workEntryBody(pending),
    });
    return afterWorkEntrySaved(appended);
  };

  const workEntryBody = (pending: NonNullable<ReturnType<typeof preparePendingEntry>>) => ({
    work_date: date,
    worker_count: workerCount,
    outputs: pending.outputs,
    intermediate_items: pending.intermediate_items,
    notes: workNotes || undefined,
  });

  const afterWorkEntrySaved = (saved: NonNullable<typeof session>) => {
    provisionalSessionRef.current = null;
    setActiveSessionId(saved.dismantling_session_id);
    setSessionSnapshot(saved);
    setWorkNotes('');
    setOutputs([emptyRow(defaultWarehouseId)]);
    setWorkerCount(DEFAULT_WORKER_COUNT);
    return saved;
  };

  /**
   * First save of a new session. With work to record, the session and its
   * first entry go in one request (one server transaction), so a refused
   * entry leaves no empty session behind; without, only the session is made.
   */
  const createWithFirstEntry = async (
    pending: NonNullable<ReturnType<typeof preparePendingEntry>>,
    options: { allowEmptyWorkEntry?: boolean } = {},
  ) => {
    const shell = provisionalSessionRef.current;
    if (shell) return appendPendingEntry(shell, pending, options);
    if (!pendingHasOutputs(pending) && !options.allowEmptyWorkEntry) return createContainer();
    const created = await create.mutateAsync({
      inbound_package_ids: pkgs.map((pkg) => pkg.inbound_package_id),
      dismantling_date: date,
      worker_count: workerCount,
      dismantling_method: method,
      outputs: [],
      notes: sessionNotes || undefined,
      first_work_entry: workEntryBody(pending),
    });
    return afterWorkEntrySaved(created);
  };

  /** Outputs need at least 1 worker; flags the field and returns false otherwise. */
  const requireWorkersForOutputs = (
    pending: NonNullable<ReturnType<typeof preparePendingEntry>>,
  ): boolean => {
    if (!pendingHasOutputs(pending) || workerCount >= 1) return true;
    setWorkerCountInvalid(true);
    setErrorMsg(
      t('efs.operations.dismantling.errors.worker_count_required', {
        defaultValue: '産出物を登録するときは作業人数（1人以上）を入力してください。',
      }),
    );
    return false;
  };

  /** Save an amendment to a confirmed session's outputs. */
  const saveAmendment = async (detail: NonNullable<typeof session>) => {
    // An amendment replaces the FINAL outputs only — the request carries
    // `outputs` and nothing else. A row switched to an intermediate would
    // therefore vanish from the final set without an intermediate ever being
    // created, silently withdrawing booked inventory. Refuse rather than lose
    // it; intermediates are raised through the daily-work flow. The kind select
    // is locked while amending, so this is the last line of defence, checked
    // before the per-field checks. No test reaches it (the UI cannot produce
    // such a row); before removing it, make sure the kind select is still
    // locked in amend mode and no seeded row can be an intermediate.
    if (outputs.some((o) => rowHasEnteredData(o) && o.output_kind !== 'final_material')) {
      setFlaggedProblems(new Map());
      setErrorMsg(t('efs.operations.dismantling.errors.amend_intermediate_unsupported'));
      return;
    }
    const cleaned = validOutputs();
    if (!cleaned) return;
    if (!allOutputsHaveWarehouse(cleaned.outputs)) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.warehouse_required', {
          defaultValue: '各産出物には実在する倉庫が必要です',
        }),
      );
      return;
    }
    if (!changeReason.trim()) {
      setErrorMsg(t('efs.operations.dismantling.errors.reason_required'));
      return;
    }
    const updated = await update.mutateAsync({
      id: detail.dismantling_session_id,
      body: {
        dismantling_method: method,
        outputs: cleaned.outputs,
        notes: sessionNotes || undefined,
        change_reason: changeReason.trim(),
      },
    });
    setSessionSnapshot(updated);
    leaveAmendMode(updated);
  };

  /** Save / Complete / confirm refusals: a cataloged server code reads in
   * the UI language (the raw text, e.g. "master not approved", meant
   * nothing on site); anything else keeps its own message. A validation
   * error keeps its own text too: the localized one points at per-field
   * errors this dialog does not show. */
  const apiErrorText = (err: unknown): string => {
    const text = localizeApiError((key, opts) => t(key, { ns: 'issues', ...(opts ?? {}) }), err);
    if (text.cataloged && text.code !== 'VALIDATION_ERROR') return text.message;
    return err instanceof Error ? err.message : String(err);
  };

  const handleSaveDraft = async () => {
    setErrorMsg(null);
    setCompleteBlocker(null);
    setIntermediatesAttention(false);
    setWorkerCountInvalid(false);
    if (amendMode && session) {
      try {
        await saveAmendment(session);
      } catch (err: unknown) {
        setErrorMsg(apiErrorText(err));
      }
      return;
    }
    if (needsLegacyOutputRepair && session) {
      try {
        await repairLegacyOutputs(session);
      } catch (err: unknown) {
        setErrorMsg(apiErrorText(err));
      }
      return;
    }
    if (saveDisabledReason) {
      setErrorMsg(saveDisabledReason);
      return;
    }
    const pending = preparePendingEntry();
    if (!pending) return;
    const nextMemberIds = memberIdsForUpdate();
    if (nextMemberIds && nextMemberIds.length === 0) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.members_required', {
          defaultValue: '拆解対象の棧板は1件以上必要です',
        }),
      );
      return;
    }
    if (!requireWorkersForOutputs(pending)) return;
    const recordsWork = workerCount >= 1;
    if (!recordsWork && !pendingHasOutputs(pending)) {
      // Nobody worked and nothing was produced: at most the settings change.
      if (!session) {
        // Creating a session now would tie these pallets to an empty shell.
        setErrorMsg(
          t('efs.operations.dismantling.errors.new_session_needs_work', {
            defaultValue: '産出物か作業人数（1人以上）を入力してください。',
          }),
        );
        return;
      }
      if (!metadataHasChanged(session)) {
        setErrorMsg(
          t('efs.operations.dismantling.errors.nothing_changed', {
            defaultValue:
              '保存する内容がありません。設定を変更するか、作業人数を入力してください。',
          }),
        );
        return;
      }
    }
    try {
      let detail: NonNullable<typeof session>;
      if (session) {
        const metadataSaved = await saveMetadata(session);
        if (!metadataSaved) return;
        detail = metadataSaved;
        if (recordsWork) {
          // At least one worker: a real work day, with or without outputs.
          detail = await appendPendingEntry(detail, pending, { allowEmptyWorkEntry: true });
        }
      } else {
        detail = recordsWork
          ? await createWithFirstEntry(pending, { allowEmptyWorkEntry: true })
          : await createContainer();
      }
      setSessionSnapshot(detail);
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err));
    }
  };

  const handleConfirm = async () => {
    setErrorMsg(null);
    setCompleteBlocker(null);
    setIntermediatesAttention(false);
    setWorkerCountInvalid(false);
    if (isConfirmed || amendMode) return;
    if (needsLegacyOutputRepair) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.legacy_repair_first', {
          defaultValue: '請先補齊舊產出物的保管場所並儲存，再登錄完成進度',
        }),
      );
      return;
    }
    if (!projectedWeightComplete) {
      setErrorMsg(
        completionDisabledReason ??
          t('efs.operations.dismantling.errors.progress_not_100', {
            current: formatRatio(currentProgress),
            projected: formatRatio(projectedProgress),
            defaultValue: '累積産出が100%に達するまで完了できません。',
          }),
      );
      return;
    }
    const pending = preparePendingEntry();
    if (!pending) return;
    // Existing sessions can confirm already-saved work without a new entry.
    // Only reject an empty new session here; saved output and inventory
    // readiness are checked below before confirmation.
    if (!session && !pendingHasOutputs(pending)) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.nothing_to_save', {
          defaultValue: '産出物を入力してください',
        }),
      );
      return;
    }
    if (!requireWorkersForOutputs(pending)) return;
    const nextMemberIds = memberIdsForUpdate();
    if (nextMemberIds && nextMemberIds.length === 0) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.members_required', {
          defaultValue: '拆解対象の棧板は1件以上必要です',
        }),
      );
      return;
    }
    try {
      let detail: NonNullable<typeof session>;
      if (session) {
        const metadataSaved = await saveMetadata(session);
        if (!metadataSaved) return;
        detail = await appendPendingEntry(metadataSaved, pending);
      } else {
        detail = await createWithFirstEntry(pending);
      }
      if (!detailReachedCompletion(detail)) {
        setErrorMsg(
          t('efs.operations.dismantling.errors.progress_not_100', {
            current: formatRatio(currentProgress),
            projected: formatRatio(ratioForKg(outputKgFromSessionDetail(detail))),
            defaultValue: '累積産出が100%に達するまで完了できません。',
          }),
        );
        return;
      }

      if (getDismantlingOperationalStatus(detail) !== 'ready_to_confirm') {
        const blockers = getDismantlingConfirmBlockers(detail);
        if (blockers.onlyDraftsRemain) {
          // Only the draft intermediates stand in the way. Confirming them
          // mints pallets, so ask first instead of doing it silently.
          setConfirmPrompt({
            sessionId: detail.dismantling_session_id,
            drafts: blockers.draftIntermediates,
          });
          return;
        }
        reportConfirmBlockers(blockers);
        return;
      }
      await confirm.mutateAsync({
        id: detail.dismantling_session_id,
        change_reason: changeReason.trim() || undefined,
      });
      onOpenChange(false);
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err));
    }
  };

  /** Tell the operator exactly what still blocks Complete. */
  const reportConfirmBlockers = (blockers: DismantlingConfirmBlockers) => {
    const reasons = confirmBlockerReasons(t, blockers);
    if (reasons.length === 0) {
      reasons.push(
        t('efs.operations.dismantling.errors.confirm_blocked_unknown', {
          defaultValue:
            'まだ完了できる状態ではありません。画面を再読み込みしてから、もう一度お試しください。',
        }),
      );
    }
    setCompleteBlocker(reasons.join(' '));
    if (blockers.draftIntermediates.length > 0 || blockers.intermediatesMissingWarehouse > 0) {
      setIntermediatesAttention(true);
    }
  };

  /** Confirm the drafts the operator just agreed to, then the session. */
  const handleConfirmPromptAccept = async () => {
    if (!confirmPrompt) return;
    const { sessionId, drafts } = confirmPrompt;
    setConfirmPrompt(null);
    setErrorMsg(null);
    setCompleteBlocker(null);
    try {
      const failures = await confirmIntermediatesBatch.mutateAsync({
        items: drafts.map((item) => ({
          id: item.dismantling_intermediate_id,
          name: item.incoming_item_name ?? item.dismantling_intermediate_id,
        })),
        sessionId,
      });
      const fresh = await fetchDismantlingSession(sessionId);
      setSessionSnapshot(fresh);
      if (failures.length > 0) {
        setErrorMsg(
          t('efs.operations.dismantling.dialog.intermediates.confirm_all_partial_failure', {
            errors: failures.map((f) => `${f.name}: ${apiErrorText(f.error)}`).join(' / '),
            defaultValue: '一部の中間品を確認できませんでした: {{errors}}',
          }),
        );
        setIntermediatesAttention(true);
        return;
      }
      if (getDismantlingOperationalStatus(fresh) !== 'ready_to_confirm') {
        reportConfirmBlockers(getDismantlingConfirmBlockers(fresh));
        return;
      }
      await confirm.mutateAsync({
        id: sessionId,
        change_reason: changeReason.trim() || undefined,
      });
      onOpenChange(false);
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err));
    }
  };

  /** Back out of the prompt: leave the drafts alone and say what is pending. */
  const handleConfirmPromptBack = () => {
    if (!confirmPrompt) return;
    const drafts = confirmPrompt.drafts;
    setConfirmPrompt(null);
    reportConfirmBlockers({
      outputsMissingWarehouse: 0,
      intermediatesMissingWarehouse: 0,
      draftIntermediates: drafts,
      onlyDraftsRemain: true,
    });
  };

  const refreshSession = async () => {
    setSessionSnapshot(undefined);
    const refreshed = await detailQuery.refetch();
    if (refreshed.data) setSessionSnapshot(refreshed.data);
  };

  const handleConfirmIntermediate = async (id: string) => {
    if (!session) return;
    setErrorMsg(null);
    try {
      await confirmIntermediate.mutateAsync({
        id,
        sessionId: session.dismantling_session_id,
      });
      await refreshSession();
    } catch (err: unknown) {
      setErrorMsg(apiErrorText(err));
    }
  };

  /**
   * Confirm every draft intermediate in one go — sessions regularly produce
   * 10+ kinds and clicking them one by one was the field complaint
   * (2026-07-31). The batch hook runs sequentially (each confirm mints a
   * pallet), never aborts on a per-item failure, and invalidates the package
   * list once; the single session refresh below is the only detail refetch.
   */
  const handleConfirmAllIntermediates = async () => {
    if (!session) return;
    setErrorMsg(null);
    const drafts = sessionIntermediates.filter((item) => item.status === 'draft');
    const failures = await confirmIntermediatesBatch.mutateAsync({
      items: drafts.map((item) => ({
        id: item.dismantling_intermediate_id,
        name: item.incoming_item_name ?? item.dismantling_intermediate_id,
      })),
      sessionId: session.dismantling_session_id,
    });
    await refreshSession();
    if (failures.length > 0) {
      setErrorMsg(
        t('efs.operations.dismantling.dialog.intermediates.confirm_all_partial_failure', {
          errors: failures.map((f) => `${f.name}: ${apiErrorText(f.error)}`).join(' / '),
          defaultValue: '一部の中間品を確認できませんでした: {{errors}}',
        }),
      );
    }
  };

  const handleVoidIntermediate = async (id: string) => {
    if (!session) return;
    if (!intermediateActionReason.trim()) {
      setErrorMsg(
        t('efs.operations.dismantling.errors.intermediate_void_reason_required', {
          defaultValue: '中間品を無効にする理由を入力してください',
        }),
      );
      return;
    }
    setErrorMsg(null);
    try {
      await voidIntermediate.mutateAsync({
        id,
        sessionId: session.dismantling_session_id,
        reason: intermediateActionReason.trim(),
      });
      setIntermediateActionReason('');
      await refreshSession();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  };

  // A history row without output can be voided while the session is a draft.
  const canVoidWorkEntries =
    isExisting && session?.status === 'imported' && hasPermission(user, 'dismantling.manage');
  const isVoidableWorkEntry = (entry: DismantlingWorkEntryDto) =>
    !entry.voided_at &&
    !entry.is_legacy_backfill &&
    entry.outputs.length === 0 &&
    (entry.intermediate_items ?? []).every((item) => item.status === 'voided');

  const handleVoidWorkEntry = async (workEntryId: string) => {
    if (!session) return;
    if (!entryVoidReason.trim()) {
      setEntryVoidError(
        t('efs.operations.dismantling.errors.work_entry_void_reason_required', {
          defaultValue: '無効にする理由を入力してください',
        }),
      );
      return;
    }
    setEntryVoidError(null);
    try {
      await voidWorkEntry.mutateAsync({
        sessionId: session.dismantling_session_id,
        workEntryId,
        reason: entryVoidReason.trim(),
      });
      setVoidingEntryId(null);
      setEntryVoidReason('');
      await refreshSession();
    } catch (err: unknown) {
      setEntryVoidError(err instanceof Error ? err.message : String(err));
    }
  };

  const busy =
    create.isPending ||
    update.isPending ||
    addWorkEntry.isPending ||
    confirm.isPending ||
    confirmIntermediate.isPending ||
    confirmIntermediatesBatch.isPending ||
    voidIntermediate.isPending ||
    voidWorkEntry.isPending ||
    // The method list still deciding what to preselect. Saving into that gap
    // would demand a method the form is a moment away from filling in.
    methodsQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] min-w-0 max-w-6xl overflow-y-auto">
        <DialogHeader className="min-w-0">
          <DialogTitle>
            {displayPackages.length > 1
              ? t('efs.operations.dismantling.dialog.title_batch', {
                  count: displayPackages.length,
                  defaultValue: 'まとめて解体登録（{{count}}件）',
                })
              : t('efs.operations.dismantling.dialog.title', {
                  code: leadPkg?.package_code ?? '',
                })}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="min-w-0">
              {t('efs.operations.dismantling.dialog.subtitle', {
                defaultValue: '選択した棧板をまとめて1組の産出物として登録します。',
              })}
              {session?.revisions && session.revisions.length > 0 ? (
                <Badge variant="outline" className="ml-2">
                  {t('efs.operations.dismantling.dialog.revisions_chip', {
                    count: session.revisions.length,
                    defaultValue: '📋 {{count}} 件',
                  })}
                </Badge>
              ) : null}
              {session ? (
                <Badge variant={isConfirmed ? 'default' : 'secondary'} className="ml-2">
                  {t(`efs.operations.dismantling.operational_statuses.${operationalStatus}`, {
                    defaultValue:
                      operationalStatus === 'draft'
                        ? '未着手'
                        : operationalStatus === 'in_progress'
                          ? '解体中'
                          : operationalStatus === 'needs_evidence'
                            ? '証跡不足'
                            : operationalStatus === 'ready_to_confirm'
                              ? '確認待ち'
                              : operationalStatus === 'confirmed'
                                ? '解体完了'
                                : 'ロック済み',
                  })}
                </Badge>
              ) : null}
              <Badge
                variant={currentProgress != null && currentProgress >= 100 ? 'default' : 'outline'}
                className="ml-2"
              >
                {t('efs.operations.dismantling.dialog.current_progress', {
                  progress: formatRatio(currentProgress),
                  defaultValue: '重量比 {{progress}}',
                })}
              </Badge>
            </div>
          </DialogDescription>
        </DialogHeader>

        {isOutputlessCompleteSession ? (
          <div
            role="alert"
            className="flex min-w-0 gap-3 rounded-md border border-amber-500/70 bg-amber-500/10 p-3 text-amber-950 dark:text-amber-100"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold">
                {t('efs.operations.dismantling.dialog.outputless_complete_warning_title', {
                  defaultValue: '産出物が保存されていません',
                })}
              </p>
              <p className="text-xs leading-5">
                {t('efs.operations.dismantling.dialog.outputless_complete_warning_body', {
                  defaultValue:
                    '重量比は100%ですが、産出物が0件です。産出物を再入力して「今回の作業を保存」を押してください。アップロード済み写真はファイル庫から再選択できます。',
                })}
              </p>
            </div>
          </div>
        ) : null}

        {/* Input pallets — package_seq (when known), item name, net_kg + Σ. */}
        <div className="min-w-0 overflow-x-auto rounded-md border border-efs-border">
          <Table className="min-w-[32rem]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">
                  {t('efs.operations.dismantling.dialog.packages.code', {
                    defaultValue: '包装コード',
                  })}
                </TableHead>
                <TableHead>
                  {t('efs.operations.dismantling.dialog.packages.item', {
                    defaultValue: '品名',
                  })}
                </TableHead>
                <TableHead className="text-right">
                  {t('efs.operations.dismantling.dialog.packages.net_kg', {
                    defaultValue: '正味 kg',
                  })}
                </TableHead>
                {canEditMembers ? <TableHead className="w-12" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayPackages.map((p) => (
                <TableRow key={p.inbound_package_id}>
                  <TableCell className="font-mono text-xs">
                    {p.package_seq != null ? `#${p.package_seq} ` : ''}
                    {p.package_code}
                  </TableCell>
                  <TableCell className="text-xs">{p.incoming_item_name}</TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {p.net_kg != null ? (
                      p.net_kg
                    ) : (
                      <Badge variant="outline">
                        {t('efs.operations.dismantling.dialog.packages.missing_weight', {
                          defaultValue: '重量なし',
                        })}
                      </Badge>
                    )}
                  </TableCell>
                  {canEditMembers ? (
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMember(p.inbound_package_id)}
                        disabled={displayPackages.length <= 1}
                        aria-label={t('efs.operations.dismantling.dialog.packages.remove_member', {
                          defaultValue: '棧板を除外',
                        })}
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-end gap-4 border-t border-efs-border px-3 py-1.5 text-xs">
            <span className="text-efs-text-muted">
              {t('efs.operations.dismantling.dialog.packages.sum_net_kg', {
                kg: totalPackageKg.toFixed(3),
                defaultValue: '棧板合計 {{kg}} kg',
              })}
            </span>
          </div>
          {canEditMembers ? (
            <div className="flex items-center gap-2 border-t border-efs-border px-3 py-2">
              <Select value={addPickerId} onValueChange={addMember}>
                <SelectTrigger className="h-8 flex-1 text-xs">
                  <SelectValue
                    placeholder={
                      addableCandidates.length === 0
                        ? t('efs.operations.dismantling.dialog.packages.add_none')
                        : t('efs.operations.dismantling.dialog.packages.add_placeholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {addableCandidates.map((c) => (
                    <SelectItem key={c.inbound_package_id} value={c.inbound_package_id}>
                      {c.package_code} — {c.incoming_item_name}
                      {c.net_kg ? `（${c.net_kg} kg）` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
        </div>

        {session ? (
          <div className="min-w-0 space-y-2">
            <p className="text-sm font-medium">
              {t('efs.operations.dismantling.dialog.history.title', {
                defaultValue: '作業履歷',
              })}
            </p>
            <div className="min-w-0 overflow-x-auto rounded-md border border-efs-border">
              <Table className="min-w-[44rem]">
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.history.date', {
                        defaultValue: '作業日',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.history.workers', {
                        defaultValue: '人數',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.history.increment', {
                        defaultValue: '本次進度',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.history.cumulative', {
                        defaultValue: '累積進度',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.history.outputs', {
                        defaultValue: '產出',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.history.notes', {
                        defaultValue: '備註',
                      })}
                    </TableHead>
                    {canVoidWorkEntries ? (
                      <TableHead className="text-right">
                        {t('efs.operations.dismantling.dialog.history.actions', {
                          defaultValue: '操作',
                        })}
                      </TableHead>
                    ) : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canVoidWorkEntries ? 7 : 6}
                        className="py-4 text-center text-xs text-efs-text-muted"
                      >
                        {t('efs.operations.dismantling.dialog.history.empty', {
                          defaultValue: '尚無每日作業紀錄',
                        })}
                      </TableCell>
                    </TableRow>
                  ) : (
                    historyRows.map((entry) => (
                      <Fragment key={entry.dismantling_work_entry_id}>
                        <TableRow
                          data-testid={`work-entry-row-${entry.dismantling_work_entry_id}`}
                          className={
                            entry.voided_at ? 'text-efs-text-muted line-through' : undefined
                          }
                        >
                          <TableCell className="text-xs">{entry.work_date}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            {entry.worker_count}
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            {t('efs.operations.dismantling.dialog.history.weight_ratio', {
                              kg: formatKg(entry.kg),
                              ratio: formatRatio(entry.incrementRatio),
                              defaultValue: '{{kg}} kg / {{ratio}}',
                            })}
                          </TableCell>
                          <TableCell className="text-right text-xs font-medium tabular-nums">
                            {t('efs.operations.dismantling.dialog.history.weight_ratio', {
                              kg: formatKg(entry.cumulativeKg),
                              ratio: formatRatio(entry.cumulativeRatio),
                              defaultValue: '{{kg}} kg / {{ratio}}',
                            })}
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            {t('efs.operations.dismantling.dialog.history.output_summary', {
                              count: entry.outputCount,
                              kg: entry.kg.toFixed(3),
                              defaultValue: '{{count}} 件 / {{kg}} kg',
                            })}
                          </TableCell>
                          <TableCell className="text-xs">
                            {entry.voided_at ? (
                              // inline-flex keeps the row's line-through off the reason
                              <span className="inline-flex flex-wrap items-center gap-1">
                                <Badge variant="outline">
                                  {t('efs.operations.dismantling.dialog.history.voided', {
                                    defaultValue: '無効',
                                  })}
                                </Badge>
                                {entry.void_reason}
                              </span>
                            ) : entry.is_legacy_backfill ? (
                              <Badge variant="outline">
                                {t('efs.operations.dismantling.dialog.history.legacy', {
                                  defaultValue: '舊資料補登',
                                })}
                              </Badge>
                            ) : (
                              (entry.notes ?? '—')
                            )}
                          </TableCell>
                          {canVoidWorkEntries ? (
                            <TableCell className="text-right">
                              {isVoidableWorkEntry(entry) &&
                              voidingEntryId !== entry.dismantling_work_entry_id ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={busy}
                                  onClick={() => {
                                    setVoidingEntryId(entry.dismantling_work_entry_id);
                                    setEntryVoidReason('');
                                    setEntryVoidError(null);
                                  }}
                                >
                                  {t('efs.operations.dismantling.dialog.history.void', {
                                    defaultValue: '無効にする',
                                  })}
                                </Button>
                              ) : null}
                            </TableCell>
                          ) : null}
                        </TableRow>
                        {voidingEntryId === entry.dismantling_work_entry_id ? (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-efs-bg-subtle">
                              <div className="flex flex-wrap items-center gap-2">
                                <Input
                                  className="max-w-sm"
                                  autoFocus
                                  aria-label={t(
                                    'efs.operations.dismantling.dialog.history.void_reason_placeholder',
                                    { defaultValue: '無効にする理由' },
                                  )}
                                  placeholder={t(
                                    'efs.operations.dismantling.dialog.history.void_reason_placeholder',
                                    { defaultValue: '無効にする理由' },
                                  )}
                                  value={entryVoidReason}
                                  onChange={(event) => setEntryVoidReason(event.target.value)}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="destructive"
                                  disabled={busy}
                                  onClick={() =>
                                    void handleVoidWorkEntry(entry.dismantling_work_entry_id)
                                  }
                                >
                                  {t('efs.operations.dismantling.dialog.history.void_confirm', {
                                    defaultValue: '無効にする',
                                  })}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  disabled={busy}
                                  onClick={() => {
                                    setVoidingEntryId(null);
                                    setEntryVoidReason('');
                                    setEntryVoidError(null);
                                  }}
                                >
                                  {t('efs.operations.dismantling.dialog.history.void_cancel', {
                                    defaultValue: 'やめる',
                                  })}
                                </Button>
                                <span className="text-xs text-efs-text-muted">
                                  {t('efs.operations.dismantling.dialog.history.void_hint', {
                                    defaultValue:
                                      '無効にした行は履歴に残りますが、作業日・人数として数えません。',
                                  })}
                                </span>
                              </div>
                              {entryVoidError ? (
                                <p role="alert" className="mt-1 text-xs font-medium text-red-600">
                                  {entryVoidError}
                                </p>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        {session && sessionIntermediates.length > 0 ? (
          <div
            ref={intermediatesSectionRef}
            className={
              intermediatesAttention
                ? 'min-w-0 space-y-2 rounded-md p-2 ring-2 ring-red-500'
                : 'min-w-0 space-y-2'
            }
          >
            {intermediatesAttention && completeBlocker ? (
              // Visual repeat next to the intermediates; the footer copy is the
              // one announced to assistive technology.
              <p aria-hidden="true" className="text-xs font-medium text-red-600">
                {completeBlocker}
              </p>
            ) : null}
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-sm font-medium">
                  {t('efs.operations.dismantling.dialog.intermediates.title', {
                    defaultValue: '再解体する中間品',
                  })}
                </p>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.dialog.intermediates.hint', {
                    defaultValue:
                      '下書きを確認すると再解体用の棧板が1件生成され、分類・解体工程へ進めます。',
                  })}
                </p>
              </div>
              {sessionIntermediates.some((item) => item.status === 'draft') ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="max-w-sm"
                    value={intermediateActionReason}
                    onChange={(event) => setIntermediateActionReason(event.target.value)}
                    placeholder={t(
                      'efs.operations.dismantling.dialog.intermediates.void_reason_placeholder',
                      { defaultValue: '無効にする場合の理由' },
                    )}
                  />
                  <Button
                    size="sm"
                    onClick={() => void handleConfirmAllIntermediates()}
                    disabled={busy}
                  >
                    {t('efs.operations.dismantling.dialog.intermediates.confirm_all', {
                      count: sessionIntermediates.filter((item) => item.status === 'draft').length,
                      defaultValue: '下書きをすべて確認（{{count}}件）',
                    })}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="min-w-0 overflow-x-auto rounded-md border border-efs-border">
              <Table className="min-w-[48rem]">
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.intermediates.item', {
                        defaultValue: '品目',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.intermediates.kg', {
                        defaultValue: '重量 kg',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.intermediates.warehouse', {
                        defaultValue: '倉庫',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.intermediates.route', {
                        defaultValue: '次の解体方式',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.dialog.intermediates.status', {
                        defaultValue: '状態',
                      })}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.dismantling.dialog.intermediates.actions', {
                        defaultValue: '操作',
                      })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessionIntermediates.map((item) => (
                    <TableRow key={item.dismantling_intermediate_id}>
                      <TableCell className="text-xs">
                        {item.incoming_item_name ?? item.incoming_item_id}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {item.weight_kg}
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.warehouse_code && item.warehouse_display_name
                          ? `${item.warehouse_code} — ${item.warehouse_display_name}`
                          : (item.warehouse_display_name ??
                            warehouses.find(
                              (warehouse) => warehouse.warehouse_id === item.warehouse_id,
                            )?.display_name ??
                            '—')}
                      </TableCell>
                      <TableCell className="text-xs">
                        {t(`efs.masters.route_type.${item.next_route_type}`, {
                          ns: 'masters',
                          defaultValue: item.next_route_type,
                        })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={item.status === 'confirmed' ? 'default' : 'secondary'}>
                          {t(
                            `efs.operations.dismantling.dialog.intermediates.statuses.${item.status}`,
                            { defaultValue: item.status },
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {item.status === 'draft' ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                void handleConfirmIntermediate(item.dismantling_intermediate_id)
                              }
                              disabled={busy}
                            >
                              {t(
                                'efs.operations.dismantling.dialog.intermediates.confirm_and_mint',
                                { defaultValue: '確認して再解体棧板を生成' },
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void handleVoidIntermediate(item.dismantling_intermediate_id)
                              }
                              disabled={busy}
                            >
                              {t('efs.common.actions.void', {
                                ns: 'common',
                                defaultValue: '無効化',
                              })}
                            </Button>
                          </div>
                        ) : item.new_inbound_package_id ? (
                          <Link
                            className="text-xs text-primary underline underline-offset-2"
                            to={derivedPalletHref(item.new_inbound_package_id)}
                          >
                            {t(
                              'efs.operations.dismantling.dialog.intermediates.open_derived_package',
                              { defaultValue: '生成した棧板を開く' },
                            )}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        {/* Session-level method sits before outputs. Daily date/workers live
            at the bottom, after operators have entered the physical weights. */}
        {!fieldsLocked ? (
          <div className="min-w-0 rounded-md border border-efs-border bg-efs-bg-subtle p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">
                {needsLegacyOutputRepair
                  ? t('efs.operations.dismantling.dialog.legacy_repair_title', {
                      defaultValue: '補齊舊產出資料',
                    })
                  : t('efs.operations.dismantling.dialog.session_settings_title', {
                      defaultValue: '解体作業設定',
                    })}
              </p>
              {needsLegacyOutputRepair ? (
                <span className="text-sm text-amber-900">
                  {t('efs.operations.dismantling.dialog.legacy_repair_hint', {
                    defaultValue: '修復完成後才能追加每日進度；本次修改會留下原因與修訂紀錄',
                  })}
                </span>
              ) : null}
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <label className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.fields.dismantling_method')}
                </label>
                {/* Operators pick a NAME the processor chose; `method_code` is
                    an internal identifier and never belongs on screen. A code
                    the master no longer offers (retired, or predating the
                    master entirely) still has to render, so it is offered as a
                    marked history entry rather than silently blanking the
                    field or leaking the raw string as if it were a label. */}
                {methodOptions.length > 0 || storedMethodOption ? (
                  <Select value={method} onValueChange={setMethod} disabled={fieldsLocked}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methodOptions.map((m) => (
                        <SelectItem key={m.method_code} value={m.method_code}>
                          {m.display_name}
                        </SelectItem>
                      ))}
                      {storedMethodOption ? (
                        <SelectItem
                          key={storedMethodOption.method_code}
                          value={storedMethodOption.method_code}
                        >
                          {t('efs.operations.dismantling.method_legacy_option', {
                            name: storedMethodOption.display_name,
                            defaultValue: '{{name}}（履歴）',
                          })}
                        </SelectItem>
                      ) : null}
                    </SelectContent>
                  </Select>
                ) : (
                  // The processor has registered nothing yet. Typing a label is
                  // allowed — the server gives the same grace — but the field
                  // starts empty: prefilling an internal code would just put a
                  // string no operator can read into the record.
                  <>
                    <Input
                      value={method}
                      onChange={(e) => setMethod(e.target.value)}
                      disabled={fieldsLocked}
                      placeholder={t('efs.operations.dismantling.method_placeholder', {
                        defaultValue: '例：手動分解',
                      })}
                    />
                    <p className="mt-1 text-xs text-efs-text-muted">
                      {t('efs.operations.dismantling.method_master_hint')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-2 min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              {t('efs.operations.dismantling.dialog.outputs_section', {
                defaultValue: '産出物',
              })}
            </p>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-efs-text-muted">
                {/* These rows are the whole corrected set while amending, not
                    an additional day's work — say which one it is. */}
                {editableRowsReplaceOutputs
                  ? t('efs.operations.dismantling.dialog.corrected_total_kg', {
                      kg: totalKg.toFixed(3),
                      defaultValue: '修正後合計 {{kg}} kg',
                    })
                  : t('efs.operations.dismantling.dialog.total_kg', {
                      kg: totalKg.toFixed(3),
                      defaultValue: '本次合計 {{kg}} kg',
                    })}
              </span>
              <span className="text-efs-text-muted">
                {t('efs.operations.dismantling.dialog.cumulative_kg', {
                  kg: (historicalKg + totalKg).toFixed(3),
                  defaultValue: '累積產出 {{kg}} kg',
                })}
              </span>
              <span
                className={
                  Math.abs(historicalKg + totalKg - totalPackageKg) < 0.001
                    ? 'text-efs-text-muted'
                    : 'text-amber-900 dark:text-amber-100'
                }
              >
                {t('efs.operations.dismantling.dialog.reconcile_diff', {
                  diff: (historicalKg + totalKg - totalPackageKg).toFixed(3),
                  defaultValue: '差分 {{diff}} kg',
                })}
              </span>
            </div>
          </div>
          {/* Recorded outputs. Confirming a session clears the editable table
              (daily work is append-only) and the history table only carries a
              "N 件 / X kg" summary, so without this the items that were
              actually produced have nowhere to show once the work is done.

              Hidden while amending: the editable table below was seeded from
              exactly these rows, so showing both puts the same output on
              screen twice — once uneditable — and the operator has to work out
              which one their edits are going into. The editable copy IS the
              recorded set at that point. */}
          {recordedOutputs.length > 0 && !editableRowsReplaceOutputs ? (
            <div
              data-testid="recorded-outputs"
              className="overflow-x-auto rounded-md border border-efs-border bg-efs-bg-subtle"
            >
              <Table className="min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">
                      {t('efs.operations.dismantling.dialog.history.date', {
                        defaultValue: '作業日',
                      })}
                    </TableHead>
                    <TableHead>
                      {t('efs.operations.dismantling.drawer.fields.item', {
                        defaultValue: '素材 / 中間品目',
                      })}
                    </TableHead>
                    <TableHead className="w-28 text-right">
                      {t('efs.operations.dismantling.drawer.fields.kg')}
                    </TableHead>
                    <TableHead className="w-44">
                      {t('efs.operations.dismantling.drawer.fields.warehouse', {
                        defaultValue: '倉庫',
                      })}
                    </TableHead>
                    <TableHead className="w-24 text-right">
                      {t('efs.operations.dismantling.drawer.fields.photos', {
                        defaultValue: '写真',
                      })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recordedOutputs.map((output) => (
                    <TableRow key={output.dismantling_output_id}>
                      <TableCell className="text-xs text-efs-text-muted">
                        {output.workDate ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs">{output.raw_material_name}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{output.kg}</TableCell>
                      <TableCell className="text-xs">
                        {output.warehouse_display_name
                          ? `${output.warehouse_code ?? ''} — ${output.warehouse_display_name}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">
                        {(output.photo_evidence_file_ids ?? []).length}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
          {!batchQuery.isLoading && !warehousesQuery.isLoading && warehouses.length === 0 ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
              {t('efs.operations.dismantling.errors.no_facility_warehouse', {
                defaultValue:
                  'この月合計表の拠点に紐づく有効な倉庫がありません。倉庫マスタの拠点設定を確認してください。',
              })}
            </p>
          ) : null}
          {!fieldsLocked ? (
            <>
              {amendMode ? (
                // Why the output kind is greyed out, and where intermediates go instead.
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.errors.amend_intermediate_unsupported')}
                </p>
              ) : null}
              {/* Two table rows per output: a compact field row (区分 / 品目 / kg /
              倉庫 / 削除) plus a full-width detail row for 再解体情報 and 写真.
              The old single-row layout forced min-w-[1320px] inside a max-w-6xl
              dialog, so the whole dialog pan-scrolled horizontally. */}
              <div className="overflow-x-auto rounded-md border border-efs-border">
                <Table className="min-w-[720px] table-fixed">
                  <TableHeader>
                    <TableRow>
                      {/* Row numbers match the "row N" in the incomplete-row messages. */}
                      <TableHead className="w-10 text-right">#</TableHead>
                      <TableHead className="w-40">
                        {t('efs.operations.dismantling.drawer.fields.output_kind', {
                          defaultValue: '産出区分',
                        })}
                      </TableHead>
                      <TableHead>
                        {t('efs.operations.dismantling.drawer.fields.item', {
                          defaultValue: '素材 / 中間品目',
                        })}
                      </TableHead>
                      <TableHead className="w-28 text-right">
                        {t('efs.operations.dismantling.drawer.fields.kg')}
                      </TableHead>
                      <TableHead className="w-52">
                        {t('efs.operations.dismantling.drawer.fields.warehouse', {
                          defaultValue: '倉庫',
                        })}
                      </TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {outputs.map((o, index) => (
                      <Fragment key={o._key}>
                        <TableRow className="border-b-0">
                          <TableCell className="text-right font-mono text-xs tabular-nums text-efs-text-muted">
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={o.output_kind}
                              onValueChange={(value) => {
                                const outputKind = value as DismantlingOutputKind;
                                updateOutput(o._key, {
                                  output_kind: outputKind,
                                  ...(outputKind === 'final_material'
                                    ? {
                                        incoming_item_id: '',
                                      }
                                    : {
                                        material_id: '',
                                        raw_material_name: '',
                                      }),
                                });
                              }}
                              // An amendment saves final materials only (intermediates
                              // are raised from daily work), so the kind stays fixed.
                              disabled={fieldsLocked || needsLegacyOutputRepair || amendMode}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {dismantlingOutputKindValues.map((kind) => (
                                  <SelectItem key={kind} value={kind}>
                                    {t(`efs.operations.dismantling.output_kinds.${kind}`, {
                                      defaultValue:
                                        kind === 'final_material'
                                          ? '最終素材'
                                          : '荷姿変更（再解体）',
                                    })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {/* Long masters lists get a text-filter combobox
                                instead of a plain Select — scrolling hundreds
                                of rows was unusable (field report 2026-07-31). */}
                            <div
                              data-output-field={`${o._key}:item`}
                              data-invalid={invalidInputs.has(`${o._key}:item`) || undefined}
                            >
                              {o.output_kind === 'final_material' ? (
                                <SearchableSelect
                                  className={
                                    invalidInputs.has(`${o._key}:item`)
                                      ? INVALID_FIELD_CLASS
                                      : undefined
                                  }
                                  options={materialOptions}
                                  value={o.material_id}
                                  onChange={(value) => {
                                    const material = materials.find(
                                      (candidate) => candidate.material_id === value,
                                    );
                                    updateOutput(o._key, {
                                      material_id: value,
                                      raw_material_name: material?.japanese_name ?? '',
                                    });
                                  }}
                                  disabled={fieldsLocked}
                                  placeholder={t(
                                    'efs.operations.dismantling.dialog.material_placeholder',
                                    {
                                      defaultValue: '素材を選択',
                                    },
                                  )}
                                />
                              ) : (
                                <SearchableSelect
                                  className={
                                    invalidInputs.has(`${o._key}:item`)
                                      ? INVALID_FIELD_CLASS
                                      : undefined
                                  }
                                  options={incomingItemOptions}
                                  value={o.incoming_item_id}
                                  onChange={(value) =>
                                    updateOutput(o._key, { incoming_item_id: value })
                                  }
                                  disabled={fieldsLocked}
                                  placeholder={t(
                                    'efs.operations.dismantling.dialog.intermediate_item_placeholder',
                                    { defaultValue: '中間品の品目を選択' },
                                  )}
                                />
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              data-output-field={`${o._key}:kg`}
                              aria-invalid={invalidInputs.has(`${o._key}:kg`)}
                              className={cn(
                                'text-right font-mono tabular-nums',
                                invalidInputs.has(`${o._key}:kg`) && INVALID_FIELD_CLASS,
                              )}
                              inputMode="decimal"
                              value={o.kg}
                              onChange={(event) => updateOutput(o._key, { kg: event.target.value })}
                              placeholder="0.000"
                              disabled={fieldsLocked}
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={o.warehouse_id ?? ''}
                              onValueChange={(value) =>
                                updateOutput(o._key, { warehouse_id: value })
                              }
                              disabled={fieldsLocked}
                            >
                              <SelectTrigger
                                data-output-field={`${o._key}:warehouse`}
                                aria-invalid={invalidInputs.has(`${o._key}:warehouse`)}
                                className={
                                  invalidInputs.has(`${o._key}:warehouse`)
                                    ? INVALID_FIELD_CLASS
                                    : undefined
                                }
                              >
                                <SelectValue
                                  placeholder={t(
                                    'efs.operations.dismantling.dialog.warehouse_placeholder',
                                    { defaultValue: '倉庫を選択' },
                                  )}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {warehouses.map((warehouse) => (
                                  <SelectItem
                                    key={warehouse.warehouse_id}
                                    value={warehouse.warehouse_id}
                                  >
                                    {warehouse.code} — {warehouse.display_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeOutput(o._key)}
                              disabled={
                                fieldsLocked || needsLegacyOutputRepair || outputs.length <= 1
                              }
                              aria-label={t('efs.operations.dismantling.dialog.remove_row', {
                                defaultValue: '行を削除',
                              })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={6} className="pt-0">
                            <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,0.9fr)]">
                              <div className="min-w-0">
                                <p className="mb-1 text-xs font-medium text-efs-text-muted">
                                  {t('efs.operations.dismantling.drawer.fields.re_dismantling', {
                                    defaultValue: '再解体情報',
                                  })}
                                </p>
                                {o.output_kind === 'intermediate' ? (
                                  <div className="space-y-2">
                                    <div>
                                      <p className="mb-1 text-xs text-efs-text-muted">
                                        {t(
                                          'efs.operations.dismantling.dialog.intermediates.route',
                                          {
                                            defaultValue: '次の解体方式',
                                          },
                                        )}
                                      </p>
                                      <Select
                                        value={o.next_route_type}
                                        onValueChange={(value) =>
                                          updateOutput(o._key, {
                                            next_route_type:
                                              value as (typeof dismantlingRouteValues)[number],
                                          })
                                        }
                                        disabled={fieldsLocked}
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {dismantlingRouteValues.map((route) => (
                                            <SelectItem key={route} value={route}>
                                              {t(`efs.masters.route_type.${route}`, {
                                                ns: 'masters',
                                                defaultValue: route,
                                              })}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-efs-text-muted">
                                    {t('efs.operations.dismantling.dialog.final_material_hint', {
                                      defaultValue: '素材在庫として倉庫へ入庫',
                                    })}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0 rounded-md border border-efs-border bg-efs-bg-panel p-2">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <p className="text-xs font-medium text-efs-text-muted">
                                    {t('efs.operations.dismantling.drawer.fields.photos', {
                                      defaultValue: '写真',
                                    })}
                                  </p>
                                  <span className="shrink-0 rounded border border-efs-border px-1.5 py-0.5 text-[11px] text-efs-text-muted">
                                    {t('efs.operations.dismantling.dialog.photo_optional_badge', {
                                      defaultValue: '任意',
                                    })}
                                  </span>
                                </div>
                                <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                                  {canReadFileLibrary ? (
                                    <FileSourceChooser
                                      value={outputPhotoSourceModes[o._key] ?? 'local'}
                                      onChange={(value) =>
                                        setOutputPhotoSourceModes((prev) => ({
                                          ...prev,
                                          [o._key]: value,
                                        }))
                                      }
                                      disabled={isConfirmed || uploadingOutputKey === o._key}
                                    />
                                  ) : null}
                                  <input
                                    ref={(node) => {
                                      fileRefs.current[o._key] = node;
                                    }}
                                    type="file"
                                    accept={EVIDENCE_MEDIA_ACCEPT}
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files.length > 0) {
                                        void handleOutputPhotos(o._key, e.target.files);
                                      }
                                      e.target.value = '';
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="min-w-0 justify-center gap-1.5 whitespace-normal"
                                    onClick={() => {
                                      if (
                                        canReadFileLibrary &&
                                        (outputPhotoSourceModes[o._key] ?? 'local') === 'library'
                                      ) {
                                        setLibraryPickerOutputKey(o._key);
                                        return;
                                      }
                                      fileRefs.current[o._key]?.click();
                                    }}
                                    disabled={isConfirmed || uploadingOutputKey === o._key}
                                  >
                                    {uploadingOutputKey === o._key ? (
                                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                                    ) : (
                                      <Upload className="h-4 w-4 shrink-0" />
                                    )}
                                    <span className="min-w-0 break-words">
                                      {canReadFileLibrary &&
                                      (outputPhotoSourceModes[o._key] ?? 'local') === 'library'
                                        ? t(
                                            'efs.operations.dismantling.dialog.choose_library_photo',
                                            {
                                              defaultValue: 'ファイル庫から選択',
                                            },
                                          )
                                        : t('efs.operations.dismantling.dialog.upload_photo', {
                                            defaultValue: '写真追加',
                                          })}
                                    </span>
                                  </Button>
                                </div>
                                <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
                                  {(o.photo_evidence_file_ids ?? []).map((fid) => (
                                    <PhotoThumb
                                      key={fid}
                                      fileId={fid}
                                      onRemove={() => removeOutputPhoto(o._key, fid)}
                                      removeLabel={t(
                                        'efs.operations.dismantling.dialog.remove_photo',
                                        {
                                          defaultValue: '写真を削除',
                                        },
                                      )}
                                    />
                                  ))}
                                  {(o.photo_evidence_file_ids ?? []).length === 0 ? (
                                    <p className="text-xs text-efs-text-muted">
                                      {t('efs.operations.dismantling.dialog.photo_optional_hint', {
                                        defaultValue: '必要な場合のみ写真を追加してください。',
                                      })}
                                    </p>
                                  ) : null}
                                  <FileLibraryPickerDialog
                                    open={libraryPickerOutputKey === o._key}
                                    onOpenChange={(nextOpen) => {
                                      if (!nextOpen) setLibraryPickerOutputKey(null);
                                    }}
                                    batchId={batchId}
                                    acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
                                    acceptFile={fileMatchesEvidenceMediaAccept}
                                    onSelect={(file) => {
                                      void handleOutputLibraryPhoto(o._key, file);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addOutput}
                disabled={fieldsLocked || needsLegacyOutputRepair}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('efs.operations.dismantling.create.add_output')}
              </Button>
            </>
          ) : null}
        </div>

        <div className="mt-2 space-y-2">
          <div>
            <label className="text-xs text-efs-text-muted">
              {t('efs.operations.dismantling.dialog.session_notes', {
                defaultValue: '整批備註',
              })}
            </label>
            <Textarea
              rows={2}
              value={sessionNotes}
              onChange={(event) => setSessionNotes(event.target.value)}
              disabled={fieldsLocked}
            />
          </div>
          {isExisting ? (
            <div>
              <label className="text-xs text-efs-text-muted">
                {t('efs.operations.dismantling.dialog.change_reason', {
                  defaultValue: '修改原因（再編集時必須）',
                })}
              </label>
              <Textarea
                rows={2}
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder={t('efs.operations.dismantling.dialog.change_reason_placeholder', {
                  defaultValue: '例: 計量誤差を修正',
                })}
                disabled={fieldsLocked}
              />
            </div>
          ) : null}
          {errorMsg ? <p className="text-xs text-red-900">{errorMsg}</p> : null}
          {!fieldsLocked && rowsWithProblems.length > 0 ? (
            <div role="alert" className="space-y-0.5 text-xs text-red-900">
              {rowsWithProblems.map((row) => (
                <p key={row.key}>
                  {t('efs.operations.dismantling.errors.output_row_problems', {
                    row: row.rowNumber,
                    fields: row.problems.map(fieldProblemLabel).join(
                      t('efs.operations.dismantling.errors.output_row_separator', {
                        defaultValue: '、',
                      }),
                    ),
                    defaultValue: '{{row}}行目：{{fields}}',
                  })}
                </p>
              ))}
            </div>
          ) : null}
          {completeBlocker ? (
            <p role="alert" className="text-xs text-red-900">
              {completeBlocker}
            </p>
          ) : null}
        </div>

        {!fieldsLocked ? (
          <div className="min-w-0 rounded-md border border-efs-border bg-efs-bg-subtle p-3">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">
                  {t('efs.operations.dismantling.dialog.daily_entry_title', {
                    defaultValue: '今回の作業を追加',
                  })}
                </p>
                <p className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.dialog.weight_formula_hint', {
                    defaultValue:
                      '進捗率は産出重量 ÷ 元パレット正味重量で自動計算します。数値は入力不要です。',
                  })}
                </p>
              </div>
              <span
                className={
                  projectedOverweight
                    ? 'text-sm font-semibold text-red-900'
                    : 'text-sm text-efs-text-muted'
                }
              >
                {t('efs.operations.dismantling.dialog.projected_progress', {
                  current: formatRatio(currentProgress),
                  projected: formatRatio(projectedProgress),
                  defaultValue: '現在 {{current}} → 保存後 {{projected}}',
                })}
              </span>
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-[repeat(2,minmax(0,1fr))_minmax(16rem,1.2fr)]">
              <div className="min-w-0">
                <label className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.fields.session_date')}
                </label>
                <DatePicker value={date} onChange={setDate} disabled={fieldsLocked} />
              </div>
              <div className="min-w-0">
                <label className="text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.fields.worker_count')}
                </label>
                <Input
                  type="number"
                  min={0}
                  value={workerCount}
                  aria-label={t('efs.operations.dismantling.fields.worker_count')}
                  aria-invalid={workerCountInvalid}
                  className={workerCountInvalid ? 'border-red-500 ring-2 ring-red-500' : undefined}
                  onChange={(e) => {
                    setWorkerCount(Number(e.target.value));
                    setWorkerCountInvalid(false);
                  }}
                  disabled={fieldsLocked}
                />
                <p className="mt-1 text-xs text-efs-text-muted">
                  {t('efs.operations.dismantling.dialog.worker_count_hint')}
                </p>
              </div>
              <div className="min-w-0 rounded-md border border-efs-border bg-efs-bg-panel p-2 text-xs">
                <dl className="grid min-w-0 grid-cols-[minmax(0,1fr)_minmax(0,max-content)] gap-x-3 gap-y-1">
                  <dt className="min-w-0 text-efs-text-muted">
                    {t('efs.operations.dismantling.dialog.weight_stats.input', {
                      defaultValue: '元パレット正味',
                    })}
                  </dt>
                  <dd className="min-w-0 text-right font-mono tabular-nums">
                    {formatKg(inputWeight)} kg
                  </dd>
                  <dt className="min-w-0 text-efs-text-muted">
                    {t('efs.operations.dismantling.dialog.weight_stats.this_entry', {
                      defaultValue: '今回の産出',
                    })}
                  </dt>
                  <dd className="min-w-0 text-right font-mono tabular-nums">
                    {formatKg(totalKg)} kg / {formatRatio(pendingProgress)}
                  </dd>
                  <dt className="min-w-0 text-efs-text-muted">
                    {t('efs.operations.dismantling.dialog.weight_stats.projected', {
                      defaultValue: '保存後累積',
                    })}
                  </dt>
                  <dd className="min-w-0 text-right font-mono tabular-nums">
                    {formatKg(projectedOutputKg)} kg / {formatRatio(projectedProgress)}
                  </dd>
                </dl>
                {inputWeight == null ? (
                  <p className="mt-2 text-amber-900 dark:text-amber-100">
                    {t('efs.operations.dismantling.dialog.weight_stats.unavailable', {
                      defaultValue:
                        '元パレットに未入力または0以下の重量があるため、重量比は表示できません。',
                    })}
                  </p>
                ) : projectedOverweight ? (
                  <p className="mt-2 text-red-900">
                    {t('efs.operations.dismantling.dialog.weight_stats.overweight', {
                      defaultValue:
                        '保存後の産出重量が元パレット重量を超えます。重量を確認してください。',
                    })}
                  </p>
                ) : null}
                {regularWorkflowVisible && completionDisabledReason ? (
                  <p className="mt-2 text-amber-900 dark:text-amber-100">
                    {completionDisabledReason}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-efs-text-muted">
                {t('efs.operations.dismantling.dialog.work_notes', {
                  defaultValue: '今回の作業メモ',
                })}
              </label>
              <Textarea
                rows={2}
                value={workNotes}
                onChange={(e) => setWorkNotes(e.target.value)}
                disabled={fieldsLocked}
              />
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('efs.common.actions.cancel', { ns: 'common' })}
          </Button>
          {isConfirmed && !amendMode ? (
            <Button type="button" variant="outline" onClick={startAmend} disabled={busy}>
              {t('efs.operations.dismantling.actions.amend_outputs', {
                defaultValue: '産出物を修正',
              })}
            </Button>
          ) : null}
          {amendMode ? (
            <Button type="button" variant="ghost" onClick={() => leaveAmendMode()} disabled={busy}>
              {t('efs.operations.dismantling.actions.cancel_amend', {
                defaultValue: '修正をやめる',
              })}
            </Button>
          ) : null}
          {showReturnToSortingAction ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setCancelDialogOpen(true)}
              disabled={busy}
            >
              <RotateCcw className="mr-1 h-4 w-4" />
              {t('efs.operations.dismantling.cancel.action', {
                defaultValue: '取消して仕分けへ戻す',
              })}
            </Button>
          ) : null}
          {!isConfirmed || amendMode || needsLegacyOutputRepair ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleSaveDraft()}
              disabled={
                busy ||
                (fieldsLocked && !amendMode) ||
                (regularWorkflowVisible && regularSaveDisabledByProgress)
              }
              title={saveDisabledReason}
            >
              {update.isPending || create.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : null}
              {amendMode
                ? t('efs.operations.dismantling.actions.save_amendment', {
                    defaultValue: '修正を保存',
                  })
                : t('efs.operations.dismantling.actions.save_work_entry', {
                    defaultValue: '保存',
                  })}
            </Button>
          ) : null}
          {regularWorkflowVisible ? (
            <Button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={busy || regularCompleteDisabledByProgress}
              title={
                regularCompleteDisabledByProgress
                  ? (completionDisabledReason ?? undefined)
                  : undefined
              }
            >
              {confirm.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {t('efs.operations.dismantling.actions.confirm_inventory', {
                defaultValue: '完了',
              })}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
      <CancelDismantlingSessionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        session={session}
        showTechnicalDetails={hasPermission(user, 'system.admin')}
        onCancelled={() => onOpenChange(false)}
      />
      <Dialog
        open={!!confirmPrompt}
        onOpenChange={(next) => {
          if (!next) handleConfirmPromptBack();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('efs.operations.dismantling.dialog.confirm_with_intermediates.title', {
                defaultValue: '中間品を確定して完了しますか？',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('efs.operations.dismantling.dialog.confirm_with_intermediates.description', {
                count: confirmPrompt?.drafts.length ?? 0,
                defaultValue:
                  '次の{{count}}件の中間品を確定すると、それぞれ再解体用のパレットが作成されます。',
              })}
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-1 text-sm">
            {(confirmPrompt?.drafts ?? []).map((item) => (
              <li
                key={item.dismantling_intermediate_id}
                className="flex flex-wrap justify-between gap-2 rounded border border-efs-border px-2 py-1"
              >
                <span>{item.incoming_item_name ?? item.incoming_item_id}</span>
                <span className="font-mono tabular-nums">
                  {formatKg(Number(item.weight_kg))} kg
                  {item.warehouse_display_name
                    ? ` · ${item.warehouse_code ?? ''} — ${item.warehouse_display_name}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-efs-text-muted">
            {t('efs.operations.dismantling.dialog.confirm_with_intermediates.void_hint', {
              defaultValue:
                '内容が違う中間品がある場合は「戻って修正」を押し、その中間品を無効にしてください。',
            })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={handleConfirmPromptBack} disabled={busy}>
              {t('efs.operations.dismantling.dialog.confirm_with_intermediates.back', {
                defaultValue: '戻って修正',
              })}
            </Button>
            <Button onClick={() => void handleConfirmPromptAccept()} disabled={busy}>
              {t('efs.operations.dismantling.dialog.confirm_with_intermediates.confirm', {
                defaultValue: '確定して完了',
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default DismantlingSessionDialog;
